import React, { useMemo, useState, useEffect, createContext, useContext } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from 'recharts';
import { ChevronDown, ChevronRight, FileSpreadsheet, FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import { exportToXlsx, exportDailyOpsPdf } from '../../utils/exporters';
import ChartBox from '../../components/ChartBox';
import DateRangePicker, { useDateRange } from '../../components/DateRangePicker';
import ExportButtons from '../../components/ExportButtons';
import HomeoToggle, { useExcludeHomeopathy, isHomeo } from '../../components/HomeoToggle';
import {
  useLeadFunnelQuery, useLeadSourcesQuery, useLeadStatusBreakdownQuery, useLeadDepartmentBreakdownQuery,
  useStaffPerformanceQuery, useStaffDetailQuery,
  useProcedureRevenueQuery, usePatientSessionsQuery, useLazyPatientSessionsQuery, usePatientEngagementQuery,
  useReceiptsReportQuery,
  useOperationalReportQuery, useNewPatientAnalysisQuery, useRenewalAnalysisQuery, useRenewalLedgerQuery,
  useGeneralPerformanceQuery, useBuildReportMutation, useClinicAnalyticsQuery,
} from '../../api/reportsApi';
import { useBillingSummaryQuery, useListInvoicesQuery, useLazyListInvoicesQuery, useUpdateInvoiceBillingMutation } from '../../api/billingApi';
import { useLazyListAppointmentsQuery } from '../../api/appointmentsApi';
import { useListSessionsQuery, useLazyListSessionsQuery } from '../../api/sessionsApi';
import { useListDoctorsQuery } from '../../api/usersApi';
import { useCallLogAnalyticsQuery, useLazyListCallLogsQuery } from '../../api/callLogsApi';
import { useGetMessageAnalyticsQuery, useListMessagesQuery, useLazyListMessagesQuery } from '../../api/communicationsApi';
import { fetchAllPages } from '../../utils/fetchAllPages';
import { SESSION_STATUS, LEAD_STATUS } from '../../utils/status';
import { fmtINR, pct, fmtDate, dayLabel, rangeLabel } from '../../utils/format';
import DoctorRevenueDrilldown from './DoctorRevenueDrilldown';
import { doctorDisplayName } from '../appointments/doctorColors';
import { procedureLabel } from '../appointments/appointmentStatus';

// One IST date range shared by every report tab. The single global picker writes here and all
// child tabs/hooks read the SAME normalized { from, to } — so no two widgets can query mismatched
// bounds. Created once at the ReportsPage root.
const ReportRangeContext = createContext(null);
function useReportRange() {
  const ctx = useContext(ReportRangeContext);
  if (!ctx) throw new Error('useReportRange must be used inside ReportsPage');
  return ctx;
}

const SOURCE_COLORS = ['#2196f3', '#10b981', '#f59e0b', '#7c3aed', '#ef4444', '#0d9488', '#d97706', '#6366f1', '#ec4899', '#84cc16'];

// The Daily Ops patient ledger renders at most this many invoices so a wide range stays
// responsive. Exports are NOT limited by it — they page through the whole result set.
const LEDGER_PAGE_SIZE = 200;

const TABS = [
  { key: 'general', label: 'General Performance' },
  { key: 'analytics', label: 'Analytics' },
  { key: 'leads', label: 'Leads' },
  { key: 'patients', label: 'Patients' },
  { key: 'new', label: 'New Patients' },
  { key: 'renewals', label: 'Renewals' },
  { key: 'staff', label: 'Staff' },
  { key: 'billing', label: 'Billing' },
  { key: 'receipts', label: 'Receipts' },
  { key: 'daily', label: 'Daily Ops' },
  { key: 'calls', label: 'Call Logs' },
  { key: 'communication', label: 'Communication' },
  { key: 'builder', label: 'Report Builder' },
];

function Kpi({ label, value, accent }) {
  return (
    <div className="kpi-card" style={{ '--kpi-accent': accent || 'var(--blue-500)' }}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
    </div>
  );
}

// Per-tab export row. The date picker + Homeopathy toggle are now GLOBAL (page header), so this is
// just a right-aligned slot for each tab's export buttons / extra controls. (`dr` prop accepted but
// ignored for backwards-compatible call sites.)
function ReportToolbar({ children }) {
  if (!children) return null;
  return (
    <div className="toolbar" style={{ justifyContent: 'flex-end', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{children}</div>
    </div>
  );
}

/**
 * Shared call-volume breakdown, rendered identically on the Call Logs tab AND the Leads tab.
 *
 * Both read the SAME endpoint with the SAME range, so the two tabs cannot disagree — previously
 * the Leads tab showed a "Phone Call" LEAD SOURCE (a count of leads, from the Leads table, dated
 * by lead creation) which people compared against call volume on the Call Logs tab and reasonably
 * concluded the CRM was wrong.
 *
 * The four buckets are mutually exclusive server-side and always sum to Total Calls.
 */
function CallBreakdown({ a, note }) {
  const total = a?.totalLogs ?? 0;
  const parts = [
    { key: 'convertedToLead', label: 'Converted to Lead', accent: 'var(--green-700)' },
    { key: 'existingPatientOnly', label: 'Existing Patient', accent: 'var(--blue-700)' },
    { key: 'existingLeadOnly', label: 'Existing Lead', accent: 'var(--teal-600)' },
    { key: 'otherCalls', label: 'Others', accent: 'var(--text-muted)' },
  ];
  const sum = parts.reduce((s, p) => s + (a?.[p.key] ?? 0), 0);

  return (
    <>
      <div className="kpi-grid" style={{ marginBottom: 8 }}>
        <Kpi label="Total Calls" value={total.toLocaleString()} accent="var(--blue-700)" />
        {parts.map((p) => (
          <Kpi key={p.key} label={p.label} value={(a?.[p.key] ?? 0).toLocaleString()} accent={p.accent} />
        ))}
      </div>
      <div className="td-muted" style={{ fontSize: '0.76rem', marginBottom: 16 }}>
        Total Calls = Converted to Lead + Existing Patient + Existing Lead + Others
        {' '}({parts.map((p) => a?.[p.key] ?? 0).join(' + ')} = {sum.toLocaleString()}).
        Each call is counted once, in that order.
        {note ? ` ${note}` : ''}
      </div>
    </>
  );
}

// High-visibility "Totaling & Summary" ledger line — totals recompute from the SAME
// filtered rows that feed the table/chart above, so figures always tie out.
function SummaryLine({ items }) {
  return (
    <div className="card card-body" style={{ marginTop: 12, display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center', fontSize: '0.84rem', fontWeight: 600 }}>
      <span style={{ textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.06em', color: 'var(--text-muted)' }}>Totaling &amp; Summary</span>
      {items.map((it) => <span key={it.label}>{it.label}: <strong>{it.value}</strong></span>)}
    </div>
  );
}

export default function ReportsPage() {
  const [tab, setTab] = useState('leads');
  // Single global IST range shared by all tabs via context (default: last 30 days).
  const dr = useDateRange('30d');
  // The global Exclude-Homeopathy toggle rides inside the shared range, so EVERY report query
  // on this page sends it to the server — it used to be honored by only a couple of views.
  const [excludeHomeo] = useExcludeHomeopathy();
  const drWithHomeo = useMemo(() => ({
    ...dr,
    range: { ...dr.range, ...(excludeHomeo ? { excludeHomeopathy: true } : {}) },
  }), [dr, excludeHomeo]);
  // Last-sync indicator: the visible data is re-queried whenever the range or tab changes, so we
  // stamp "now" on those transitions to show how fresh the on-screen figures are.
  const [syncedAt, setSyncedAt] = useState(() => new Date());
  useEffect(() => { setSyncedAt(new Date()); }, [dr.range.from, dr.range.to, tab]);

  return (
    <ReportRangeContext.Provider value={drWithHomeo}>
      <div className="page-header">
        <div className="breadcrumb"><span>Home</span><span className="bc-sep">/</span><span className="bc-cur">Reports</span></div>
        <div className="page-header-row">
          <div>
            <h1>Reports &amp; Analytics</h1>
            <div className="page-header-sub">Leads, patients, acquisition, renewals, staff and finances.</div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <DateRangePicker
              preset={dr.preset} onPresetChange={dr.setPreset}
              customFrom={dr.customFrom} customTo={dr.customTo} onCustomChange={dr.onCustomChange}
            />
            <HomeoToggle />
            <span className="td-muted" style={{ fontSize: '0.74rem', whiteSpace: 'nowrap' }}>
              Updated {syncedAt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          </div>
        </div>
      </div>

      <div className="tabs">
        {TABS.map((t) => (
          <button key={t.key} className={`tab-btn ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'general' && <GeneralPerformanceReport />}
      {tab === 'analytics' && <ClinicAnalyticsReport />}
      {tab === 'leads' && <LeadsReport />}
      {tab === 'patients' && <PatientReport />}
      {tab === 'new' && <NewPatientReport />}
      {tab === 'renewals' && <RenewalReport />}
      {tab === 'staff' && <StaffReport />}
      {tab === 'billing' && <BillingReport />}
      {tab === 'receipts' && <ReceiptsReport />}
      {tab === 'daily' && <DailyOpsReport />}
      {tab === 'calls' && <CallLogsReport />}
      {tab === 'communication' && <CommunicationReport />}
      {tab === 'builder' && <ReportBuilder />}
    </ReportRangeContext.Provider>
  );
}

// ─── General clinic performance ───
function GeneralPerformanceReport() {
  const dr = useReportRange();
  const { data: r } = useGeneralPerformanceQuery(dr.range);
  const byDay = (r?.newPatientsByDay ?? []).map((d) => ({ label: dayLabel(d.date), count: d.count }));
  return (
    <>
      <ReportToolbar dr={dr}>
        <ExportButtons filename="general-performance" title="General Clinic Performance"
          subtitle={rangeLabel(dr.range.from, dr.range.to)}
          getSections={() => [
            {
              kind: 'kpi',
              title: 'Headline metrics',
              items: [
                { label: 'New Patients', value: r?.newPatients ?? 0 },
                { label: 'New Billings (count)', value: r?.newBillingCount ?? 0 },
                { label: 'New Billing Amount', value: fmtINR(r?.newBillingAmount) },
                { label: 'Renewed Patients', value: r?.renewedPatients ?? 0 },
                { label: 'Renewed Revenue', value: fmtINR(r?.renewedRevenue) },
              ],
            },
            {
              // The per-day series was previously visible on screen but absent from every export.
              title: 'New patients per day',
              columns: [
                { key: 'label', label: 'Day' },
                // Daily intake average is a real staffing signal, so this one opts in.
                { key: 'count', label: 'New patients', numeric: true, avg: true },
              ],
              rows: byDay,
            },
          ]} />
      </ReportToolbar>
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <Kpi label="New Patients" value={r?.newPatients ?? 0} accent="var(--blue-700)" />
        <Kpi label="New Billings" value={r?.newBillingCount ?? 0} />
        <Kpi label="New Billing Amount" value={fmtINR(r?.newBillingAmount)} accent="var(--green-500)" />
        <Kpi label="Renewed Patients" value={r?.renewedPatients ?? 0} accent="var(--teal-600)" />
        <Kpi label="Renewed Revenue" value={fmtINR(r?.renewedRevenue)} accent="var(--green-700)" />
      </div>
      <div className="card">
        <div className="card-header"><h3>New patients per day</h3></div>
        <div className="card-body">
          <ChartBox height={300}>
            <BarChart data={byDay}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" fontSize={11} /><YAxis fontSize={11} allowDecimals={false} />
              <Tooltip /><Bar dataKey="count" name="New patients" fill="var(--blue-500)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartBox>
        </div>
      </div>

      <SummaryLine items={[
        { label: 'New patients', value: (r?.newPatients ?? 0).toLocaleString() },
        { label: 'New billings', value: (r?.newBillingCount ?? 0).toLocaleString() },
        { label: 'New billing amount', value: fmtINR(r?.newBillingAmount) },
        { label: 'Renewed patients', value: (r?.renewedPatients ?? 0).toLocaleString() },
        { label: 'Renewed revenue', value: fmtINR(r?.renewedRevenue) },
      ]} />
    </>
  );
}

// ─── Clinic analytics: retention/utilization KPIs + debt-clearance interval chart ───
function ClinicAnalyticsReport() {
  const dr = useReportRange();
  const { data: a } = useClinicAnalyticsQuery(dr.range);
  const debt = (a?.debtClearanceByMonth ?? []).map((d) => ({ label: d.label, avg: d.avgSessions, patients: d.patients }));
  return (
    <>
      <ReportToolbar dr={dr}>
        <ExportButtons filename="clinic-analytics" title="Clinic Analytics"
          subtitle={rangeLabel(dr.range.from, dr.range.to)}
          getSections={() => [
            {
              // All six KPI cards were on screen but exported nowhere.
              kind: 'kpi',
              title: 'Clinic analytics',
              items: [
                { label: 'Active retention rate', value: pct(a?.activeRetentionRate) },
                { label: 'Utilization rate', value: pct(a?.utilizationRate) },
                { label: 'Avg sessions to clear dues', value: a?.averageSessionsToClearDues ?? 0 },
                { label: 'Patients cleared dues', value: a?.patientsClearedDues ?? 0 },
                { label: 'Active patients', value: a?.activePatients ?? 0 },
                { label: 'Sessions completed', value: a?.totalSessionsCompleted ?? 0 },
                { label: 'Sessions purchased', value: a?.totalSessionsPurchased ?? 0 },
              ],
            },
            {
              title: 'Average sessions to clear dues (by month)',
              columns: [
                { key: 'label', label: 'Month' },
                // "Avg sessions to clear" is already a per-month average — summing months is
                // meaningless, but the mean across months is the headline figure people quote.
                { key: 'avg', label: 'Avg sessions to clear', numeric: true, total: false, avg: true },
                { key: 'patients', label: 'Patients cleared', numeric: true },
              ],
              rows: debt.map((d) => ({ label: d.label, avg: d.avg, patients: d.patients })),
            },
          ]} />
      </ReportToolbar>

      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <Kpi label="Active retention rate" value={pct(a?.activeRetentionRate)} accent="var(--green-600)" />
        <Kpi label="Utilization rate" value={pct(a?.utilizationRate)} accent="var(--blue-700)" />
        <Kpi label="Avg sessions to clear dues" value={a?.averageSessionsToClearDues ?? 0} accent="var(--amber-500)" />
        <Kpi label="Patients cleared dues" value={a?.patientsClearedDues ?? 0} />
        <Kpi label="Active patients" value={a?.activePatients ?? 0} accent="var(--teal-600)" />
        <Kpi label="Sessions completed / purchased" value={`${a?.totalSessionsCompleted ?? 0} / ${a?.totalSessionsPurchased ?? 0}`} />
      </div>

      <div className="card">
        <div className="card-header"><h3>Average sessions to clear dues (by month)</h3></div>
        <div className="card-body">
          <ChartBox height={320}>
            <LineChart data={debt}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" fontSize={11} /><YAxis fontSize={11} allowDecimals={false} />
              <Tooltip /><Legend />
              <Line type="monotone" dataKey="avg" name="Avg sessions to clear" stroke="var(--amber-600)" strokeWidth={2} />
              <Line type="monotone" dataKey="patients" name="Patients cleared" stroke="var(--blue-500)" strokeWidth={2} />
            </LineChart>
          </ChartBox>
        </div>
      </div>

      <SummaryLine items={[
        { label: 'Active retention', value: pct(a?.activeRetentionRate) },
        { label: 'Utilization', value: pct(a?.utilizationRate) },
        { label: 'Avg sessions to clear', value: a?.averageSessionsToClearDues ?? 0 },
        { label: 'Patients cleared', value: (a?.patientsClearedDues ?? 0).toLocaleString() },
      ]} />
    </>
  );
}

// ─── Ad-hoc report builder ───
const BUILDER_STREAMS = {
  patients: { label: 'Patients', groups: [['status', 'Status'], ['gender', 'Gender'], ['city', 'City'], ['month', 'Created month']], measures: [['count', 'Count']] },
  appointments: { label: 'Appointments', groups: [['status', 'Status'], ['doctor', 'Doctor'], ['type', 'Type'], ['day', 'Day'], ['month', 'Month']], measures: [['count', 'Count']] },
  billing: { label: 'Billing', groups: [['status', 'Status'], ['month', 'Month'], ['day', 'Day']], measures: [['count', 'Invoices'], ['sum', 'Amount collected']] },
  sessions: { label: 'Sessions', groups: [['status', 'Status'], ['therapist', 'Therapist'], ['month', 'Month']], measures: [['count', 'Count']] },
};
const CHART_TYPES = [['bar', 'Bar'], ['line', 'Line'], ['pie', 'Pie']];

function ReportBuilder() {
  const dr = useReportRange();
  const [stream, setStream] = useState('appointments');
  const [groupBy, setGroupBy] = useState('status');
  const [measure, setMeasure] = useState('count');
  const [chart, setChart] = useState('bar');
  const [build, { data, isLoading }] = useBuildReportMutation();

  const [excludeHomeo] = useExcludeHomeopathy();
  const cfg = BUILDER_STREAMS[stream];
  const run = () => build({ dataStream: stream, groupBy, measure, from: dr.range.from, to: dr.range.to });
  // Homeopathy omission applies to the result rows; chart + grid + summary all derive from `rows`.
  const rows = useMemo(() => {
    const raw = data?.rows ?? [];
    return excludeHomeo ? raw.filter((r) => !isHomeo(r.label)) : raw;
  }, [data, excludeHomeo]);
  const valueLabel = data?.valueLabel ?? 'Value';

  const onStream = (s) => {
    setStream(s);
    setGroupBy(BUILDER_STREAMS[s].groups[0][0]);
    if (!BUILDER_STREAMS[s].measures.some((m) => m[0] === measure)) setMeasure(BUILDER_STREAMS[s].measures[0][0]);
  };

  return (
    <>
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <select className="form-control" style={{ width: 150 }} value={stream} onChange={(e) => onStream(e.target.value)}>
          {Object.entries(BUILDER_STREAMS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select className="form-control" style={{ width: 150 }} value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
          {cfg.groups.map(([k, l]) => <option key={k} value={k}>Group: {l}</option>)}
        </select>
        <select className="form-control" style={{ width: 160 }} value={measure} onChange={(e) => setMeasure(e.target.value)}>
          {cfg.measures.map(([k, l]) => <option key={k} value={k}>Measure: {l}</option>)}
        </select>
        <select className="form-control" style={{ width: 110 }} value={chart} onChange={(e) => setChart(e.target.value)}>
          {CHART_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <button className="btn btn-primary btn-sm" onClick={run} disabled={isLoading}>{isLoading ? 'Building…' : 'Run'}</button>
        {rows.length > 0 && (
          <ExportButtons filename={`report-${stream}-${groupBy}`} title={`${cfg.label} by ${groupBy}`}
            subtitle={rangeLabel(dr.range.from, dr.range.to)}
            getSections={() => [{
              title: `${cfg.label} by ${groupBy} — ${valueLabel}`,
              columns: [
                { key: 'label', label: groupBy },
                // A "sum" measure is money; a "count" measure is not.
                { key: 'value', label: valueLabel, numeric: true, money: measure === 'sum' },
              ],
              rows: rows.map((x) => ({ label: x.label, value: x.value })),
            }]} />
        )}
      </div>

      {rows.length === 0 ? (
        <div className="empty-state"><h3>Build a report</h3><p>Pick a data stream, dimension and measure, then Run.</p></div>
      ) : (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header"><h3>{cfg.label} by {groupBy} — {valueLabel}</h3></div>
            <div className="card-body">
              <ChartBox height={320}>
                {chart === 'pie' ? (
                  <PieChart>
                    <Pie data={rows} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={110} label>
                      {rows.map((_, i) => <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip /><Legend />
                  </PieChart>
                ) : chart === 'line' ? (
                  <LineChart data={rows}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" fontSize={11} /><YAxis fontSize={11} />
                    <Tooltip /><Line type="monotone" dataKey="value" name={valueLabel} stroke="var(--blue-600)" strokeWidth={2} />
                  </LineChart>
                ) : (
                  <BarChart data={rows}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" fontSize={11} /><YAxis fontSize={11} />
                    <Tooltip /><Bar dataKey="value" name={valueLabel} fill="var(--blue-500)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ChartBox>
            </div>
          </div>
          <div className="card">
            <div className="tbl-wrap" style={{ border: 'none' }}>
              <table>
                <thead><tr><th>{groupBy}</th><th>{valueLabel}</th></tr></thead>
                <tbody>
                  {rows.map((x) => <tr key={x.label}><td className="td-bold">{x.label}</td><td>{x.value}</td></tr>)}
                </tbody>
              </table>
            </div>
          </div>

          <SummaryLine items={[
            { label: 'Groups', value: rows.length },
            { label: `Total ${valueLabel.toLowerCase()}`, value: rows.reduce((s, r) => s + (Number(r.value) || 0), 0).toLocaleString('en-IN') },
          ]} />
        </>
      )}
    </>
  );
}

// ─── Leads ───
function LeadsReport() {
  const dr = useReportRange();
  const { data: funnel = [] } = useLeadFunnelQuery(dr.range);
  const { data: rawSources = [] } = useLeadSourcesQuery(dr.range);
  const { data: statusBreakdown = [] } = useLeadStatusBreakdownQuery(dr.range);
  const { data: departments = [] } = useLeadDepartmentBreakdownQuery(dr.range);
  // Same endpoint and range as the Call Logs tab, so the two can never disagree.
  const { data: callAnalytics } = useCallLogAnalyticsQuery(dr.range);
  const statusTotal = statusBreakdown.reduce((s, x) => s + x.count, 0);
  const departmentTotal = departments.reduce((s, x) => s + x.total, 0);

  // "Phone Call" here is a LEAD SOURCE — leads whose origin was recorded as a call — not call
  // volume. Labelling it plainly stops it being read as a contradiction of the call figures above.
  const sources = useMemo(
    () => rawSources.map((s) => (s.source === 'PhoneCall'
      ? { ...s, source: 'Phone Call (lead source)' }
      : s)),
    [rawSources],
  );
  const sourceTotal = sources.reduce((s, x) => s + x.total, 0);

  // Export mirrors the whole tab: call buckets, funnel, status, departments and sources — each a
  // titled section with its own totals, instead of the single source table it used to emit.
  const getSections = () => [
    {
      kind: 'kpi',
      title: 'Call volume (call logs)',
      items: [
        { label: 'Total Calls', value: callAnalytics?.totalLogs ?? 0 },
        { label: 'Converted to Lead', value: callAnalytics?.convertedToLead ?? 0 },
        { label: 'Existing Patient', value: callAnalytics?.existingPatientOnly ?? 0 },
        { label: 'Existing Lead', value: callAnalytics?.existingLeadOnly ?? 0 },
        { label: 'Others', value: callAnalytics?.otherCalls ?? 0 },
      ],
    },
    // Column flags below follow one rule: TOTAL wherever a sum is a real quantity, and no AVERAGE
    // at all on this tab. The mean number of leads per funnel stage, per status or per department
    // is not a figure anyone acts on — it is an artefact of how many buckets happen to exist.
    {
      title: 'Lead funnel',
      columns: [{ key: 'label', label: 'Stage' }, { key: 'count', label: 'Leads', numeric: true }],
      rows: funnel.map((f) => ({ label: f.label, count: f.count })),
    },
    {
      title: 'Leads by status',
      columns: [
        { key: 'label', label: 'Status' },
        { key: 'count', label: 'Count', numeric: true },
        // Shares already sum to 100 by construction — totalling them restates the definition.
        { key: 'share', label: 'Share (%)', numeric: true, total: false },
      ],
      rows: statusBreakdown.map((s) => ({ label: s.label, count: s.count, share: s.percentage })),
    },
    {
      title: 'Leads by department',
      columns: [
        { key: 'department', label: 'Department' },
        { key: 'total', label: 'Total leads', numeric: true },
        { key: 'share', label: 'Share (%)', numeric: true, total: false },
        { key: 'converted', label: 'Converted', numeric: true },
        // Rate is a ratio: summing it is meaningless, and an unweighted mean of per-department
        // rates is misleading (a 1-lead department would count as much as a 500-lead one).
        // The honest overall rate is total converted ÷ total leads, shown in the summary line.
        { key: 'rate', label: 'Conversion rate', numeric: false },
        { key: 'topStatus', label: 'Top status' },
      ],
      rows: departments.map((d) => ({
        department: d.department, total: d.total, share: d.percentage, converted: d.converted,
        rate: pct(d.conversionRate),
        topStatus: d.byStatus?.[0] ? `${d.byStatus[0].label} (${d.byStatus[0].count})` : '—',
      })),
      totals: [{
        label: 'Overall conversion rate',
        value: pct(departmentTotal === 0 ? 0 : departments.reduce((s, d) => s + d.converted, 0) / departmentTotal),
      }],
    },
    {
      title: 'Source conversion detail',
      columns: [
        { key: 'source', label: 'Source' },
        { key: 'total', label: 'Total leads', numeric: true },
        { key: 'converted', label: 'Converted', numeric: true },
        { key: 'rate', label: 'Conversion rate', numeric: false },
      ],
      rows: sources.map((s) => ({ source: s.source, total: s.total, converted: s.converted, rate: pct(s.conversionRate) })),
      totals: [{
        label: 'Overall conversion rate',
        value: pct(sourceTotal === 0 ? 0 : sources.reduce((s, x) => s + x.converted, 0) / sourceTotal),
      }],
    },
  ];

  return (
    <>
      <ReportToolbar dr={dr}>
        <ExportButtons filename="leads-report" title="Leads Report"
          subtitle={rangeLabel(dr.range.from, dr.range.to)}
          orientation="landscape"
          getSections={getSections} />
      </ReportToolbar>

      <CallBreakdown
        a={callAnalytics}
        note="These are call-log figures. The “Phone Call (lead source)” slice below is a different measure — leads whose recorded origin was a call — so the two will not match."
      />

      <div className="g-2" style={{ marginBottom: 18 }}>
        <div className="card">
          <div className="card-header"><h3>Lead funnel</h3></div>
          <div className="card-body">
            <ChartBox height={340}>
              <BarChart data={funnel} layout="vertical" margin={{ left: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis type="number" fontSize={11} allowDecimals={false} />
                <YAxis type="category" dataKey="label" fontSize={11} width={150} />
                <Tooltip />
                <Bar dataKey="count" fill="var(--blue-700)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ChartBox>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3>Lead sources</h3></div>
          <div className="card-body">
            <ChartBox height={340}>
              <PieChart>
                <Pie data={sources} dataKey="total" nameKey="source" cx="50%" cy="50%" outerRadius={110} label>
                  {sources.map((_, i) => <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />)}
                </Pie>
                <Tooltip /><Legend />
              </PieChart>
            </ChartBox>
          </div>
        </div>
      </div>
      {/* Status breakdown — counts + percentages per current lead status (incl. Not Converted). */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header"><h3>Leads by status</h3>
          <span className="td-muted" style={{ fontSize: '0.78rem' }}>{statusTotal.toLocaleString()} leads in range</span>
        </div>
        <div className="g-2">
          <div className="card-body">
            <ChartBox height={300}>
              <BarChart data={statusBreakdown} layout="vertical" margin={{ left: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis type="number" fontSize={11} allowDecimals={false} />
                <YAxis type="category" dataKey="label" fontSize={11} width={140} />
                <Tooltip formatter={(v, n, p) => [`${v} (${p.payload.percentage}%)`, 'Leads']} />
                <Bar dataKey="count" fill="var(--teal-600, #0d9488)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ChartBox>
          </div>
          <div className="tbl-wrap" style={{ border: 'none' }}>
            <table>
              <thead><tr><th>Status</th><th style={{ textAlign: 'right' }}>Count</th><th style={{ textAlign: 'right' }}>Share</th></tr></thead>
              <tbody>
                {statusBreakdown.map((s) => (
                  <tr key={s.status}>
                    <td><span className={`badge ${LEAD_STATUS[s.status]?.color || 'b-gray'}`}>{s.label}</span></td>
                    <td style={{ textAlign: 'right' }} className="td-bold">{s.count.toLocaleString()}</td>
                    <td style={{ textAlign: 'right' }} className="td-muted">{s.percentage}%</td>
                  </tr>
                ))}
                {statusBreakdown.length === 0 && <tr><td colSpan={3} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>No leads in range.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Department (Total Transformation / Physiotherapy / Functional / Home Visit) ── */}
      <div className="g-2" style={{ marginBottom: 18 }}>
        <div className="card">
          <div className="card-header"><h3>Leads by department</h3></div>
          <div className="card-body">
            <ChartBox height={300}>
              <BarChart data={departments} margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="department" fontSize={11} interval={0} angle={-12} textAnchor="end" height={60} />
                <YAxis fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="total" name="Leads" fill="var(--blue-700)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="converted" name="Converted" fill="var(--green-600)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartBox>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3>Department share</h3></div>
          <div className="card-body">
            <ChartBox height={300}>
              <PieChart>
                <Pie data={departments} dataKey="total" nameKey="department" cx="50%" cy="50%" outerRadius={100} label>
                  {departments.map((_, i) => <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />)}
                </Pie>
                <Tooltip /><Legend />
              </PieChart>
            </ChartBox>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header">
          <h3>Department detail</h3>
          <span className="td-muted" style={{ fontSize: '0.78rem', marginLeft: 'auto' }}>
            {departmentTotal.toLocaleString()} leads in range
          </span>
        </div>
        <div className="tbl-wrap" style={{ border: 'none' }}>
          <table>
            <thead>
              <tr>
                <th>Department</th>
                <th style={{ textAlign: 'right' }}>Total leads</th>
                <th style={{ textAlign: 'right' }}>Share</th>
                <th style={{ textAlign: 'right' }}>Converted</th>
                <th style={{ textAlign: 'right' }}>Conversion rate</th>
                <th>Top status</th>
              </tr>
            </thead>
            <tbody>
              {departments.map((d) => (
                <tr key={d.department}>
                  <td className="td-bold">{d.department}</td>
                  <td style={{ textAlign: 'right' }}>{d.total.toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }} className="td-muted">{d.percentage}%</td>
                  <td style={{ textAlign: 'right' }}>{d.converted.toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}><span className="badge b-blue">{pct(d.conversionRate)}</span></td>
                  <td className="td-muted">
                    {d.byStatus?.[0] ? `${d.byStatus[0].label} (${d.byStatus[0].count})` : '—'}
                  </td>
                </tr>
              ))}
              {departments.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>No department data.</td></tr>}
            </tbody>
            {departments.length > 0 && (
              <tfoot>
                <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                  <td>Total</td>
                  <td style={{ textAlign: 'right' }}>{departmentTotal.toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>100%</td>
                  <td style={{ textAlign: 'right' }}>{departments.reduce((s, d) => s + d.converted, 0).toLocaleString()}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h3>Source conversion detail</h3></div>
        <div className="tbl-wrap" style={{ border: 'none' }}>
          <table>
            <thead><tr><th>Source</th><th>Total leads</th><th>Converted</th><th>Conversion rate</th></tr></thead>
            <tbody>
              {sources.map((s) => (
                <tr key={s.source}>
                  <td className="td-bold">{s.source}</td><td>{s.total}</td><td>{s.converted}</td>
                  <td><span className="badge b-blue">{pct(s.conversionRate)}</span></td>
                </tr>
              ))}
              {sources.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>No source data.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ─── Receipts (cash actually collected, by payment date) ───
function ReceiptsReport() {
  const dr = useReportRange();
  const { data: r, isFetching } = useReceiptsReportQuery(dr.range);

  const daySeries = useMemo(
    () => (r?.byDay ?? []).map((d) => ({ label: dayLabel(d.date), amount: d.amount, count: d.count })),
    [r],
  );

  const getSections = () => [
    {
      kind: 'kpi',
      title: 'Collections summary',
      items: [
        { label: 'Receipts', value: r?.receiptCount ?? 0 },
        { label: 'Total collected', value: fmtINR(r?.totalCollected) },
        { label: 'Patients paying', value: r?.distinctPatients ?? 0 },
        { label: 'Average receipt value', value: fmtINR(r?.averageReceiptValue) },
        { label: 'Largest receipt', value: fmtINR(r?.largestReceipt) },
      ],
    },
    {
      title: 'Collections by payment method',
      columns: [
        { key: 'method', label: 'Method' },
        { key: 'count', label: 'Receipts', numeric: true },
        { key: 'amount', label: 'Amount', numeric: true, money: true },
        // Shares sum to 100 by construction — totalling them restates the definition.
        { key: 'share', label: 'Share (%)', numeric: true, total: false },
      ],
      rows: (r?.byMethod ?? []).map((m) => ({
        method: m.method, count: m.count, amount: m.amount, share: m.percentage,
      })),
    },
    {
      title: 'Collections by day',
      columns: [
        { key: 'label', label: 'Day' },
        { key: 'count', label: 'Receipts', numeric: true },
        // Daily average collection is genuinely useful for staffing/cash-flow, so it opts in.
        { key: 'amount', label: 'Collected', numeric: true, money: true, avg: true },
      ],
      rows: daySeries,
    },
    {
      title: 'Receipts — detail',
      columns: [
        { key: 'receipt', label: 'Receipt No.' },
        { key: 'date', label: 'Date' },
        { key: 'code', label: 'Patient ID' },
        { key: 'patient', label: 'Patient' },
        { key: 'invoice', label: 'Invoice No.' },
        { key: 'method', label: 'Method' },
        { key: 'amount', label: 'Amount', numeric: true, money: true },
      ],
      rows: (r?.receipts ?? []).map((x) => ({
        receipt: x.receiptNumber || '—',
        date: fmtDate(x.paidAt),
        code: x.patientCode,
        patient: x.patientName,
        invoice: x.invoiceNumber || '—',
        method: x.method,
        amount: x.amount,
      })),
    },
  ];

  if (isFetching && !r) {
    return (
      <>
        <div className="kpi-grid" style={{ marginBottom: 16 }}>
          {Array.from({ length: 5 }).map((_, i) => <div key={i} className="skeleton" style={{ height: 80 }} />)}
        </div>
        <div className="skeleton" style={{ height: 300 }} />
      </>
    );
  }

  return (
    <>
      <ReportToolbar dr={dr}>
        <ExportButtons filename="receipts-report" title="Receipts Report"
          subtitle={rangeLabel(dr.range.from, dr.range.to)}
          orientation="landscape"
          getSections={getSections} />
      </ReportToolbar>

      <div className="kpi-grid" style={{ marginBottom: 8 }}>
        <Kpi label="Receipts" value={(r?.receiptCount ?? 0).toLocaleString()} accent="var(--blue-700)" />
        <Kpi label="Total collected" value={fmtINR(r?.totalCollected)} accent="var(--green-700)" />
        <Kpi label="Patients paying" value={(r?.distinctPatients ?? 0).toLocaleString()} />
        <Kpi label="Average receipt" value={fmtINR(r?.averageReceiptValue)} accent="var(--teal-600)" />
        <Kpi label="Largest receipt" value={fmtINR(r?.largestReceipt)} />
      </div>
      <div className="td-muted" style={{ fontSize: '0.76rem', marginBottom: 16 }}>
        Counts money <strong>received</strong> in this range, by payment date. The Billing tab counts
        invoices <strong>issued</strong> in the range instead, so a payment made this month against
        an older invoice appears here but not there — the two are not meant to match. A dues
        settlement writes one receipt per invoice, so a single payment can appear as several rows.
      </div>

      <div className="g-2" style={{ marginBottom: 18 }}>
        <div className="card">
          <div className="card-header"><h3>Collections by day</h3></div>
          <div className="card-body">
            <ChartBox height={300}>
              <LineChart data={daySeries} margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="label" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip formatter={(v, n) => (n === 'Collected' ? fmtINR(v) : v)} />
                <Line type="monotone" dataKey="amount" name="Collected" stroke="var(--green-600)" strokeWidth={2} dot={false} />
              </LineChart>
            </ChartBox>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3>Collections by payment method</h3></div>
          <div className="card-body">
            <ChartBox height={300}>
              <BarChart data={r?.byMethod ?? []} margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="method" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip formatter={(v) => fmtINR(v)} />
                <Bar dataKey="amount" name="Collected" fill="var(--blue-700)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartBox>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header"><h3>Payment method breakdown</h3></div>
        <div className="tbl-wrap" style={{ border: 'none' }}>
          <table>
            <thead>
              <tr>
                <th>Method</th>
                <th style={{ textAlign: 'right' }}>Receipts</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th style={{ textAlign: 'right' }}>Share</th>
              </tr>
            </thead>
            <tbody>
              {(r?.byMethod ?? []).map((m) => (
                <tr key={m.method}>
                  <td className="td-bold">{m.method}</td>
                  <td style={{ textAlign: 'right' }}>{m.count.toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{fmtINR(m.amount)}</td>
                  <td style={{ textAlign: 'right' }} className="td-muted">{m.percentage}%</td>
                </tr>
              ))}
              {(r?.byMethod ?? []).length === 0 && (
                <tr><td colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>No receipts in range.</td></tr>
              )}
            </tbody>
            {(r?.byMethod ?? []).length > 0 && (
              <tfoot>
                <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                  <td>Total</td>
                  <td style={{ textAlign: 'right' }}>{(r?.receiptCount ?? 0).toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{fmtINR(r?.totalCollected)}</td>
                  <td style={{ textAlign: 'right' }}>100%</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Receipts</h3>
          <span className="td-muted" style={{ fontSize: '0.78rem', marginLeft: 'auto' }}>
            {(r?.receiptCount ?? 0).toLocaleString()} receipt{(r?.receiptCount ?? 0) !== 1 ? 's' : ''} · {rangeLabel(dr.range.from, dr.range.to)}
          </span>
        </div>
        <div className="tbl-wrap" style={{ border: 'none' }}>
          <table>
            <thead>
              <tr>
                <th>Receipt No.</th><th>Date</th><th>Patient ID</th><th>Patient</th>
                <th>Invoice No.</th><th>Method</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {(r?.receipts ?? []).map((x) => (
                <tr key={x.paymentId}>
                  <td className="td-mono">{x.receiptNumber || '—'}</td>
                  <td className="td-muted">{fmtDate(x.paidAt)}</td>
                  <td className="td-mono">{x.patientCode}</td>
                  <td className="td-bold">{x.patientName}</td>
                  <td className="td-mono">{x.invoiceNumber || '—'}</td>
                  <td><span className="badge b-gray">{x.method}</span></td>
                  <td style={{ textAlign: 'right' }} className="td-bold">{fmtINR(x.amount)}</td>
                </tr>
              ))}
              {(r?.receipts ?? []).length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>No receipts in range.</td></tr>
              )}
            </tbody>
            {(r?.receipts ?? []).length > 0 && (
              <tfoot>
                <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                  <td colSpan={6}>Total collected</td>
                  <td style={{ textAlign: 'right' }}>{fmtINR(r?.totalCollected)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <SummaryLine items={[
        { label: 'Receipts', value: (r?.receiptCount ?? 0).toLocaleString() },
        { label: 'Total collected', value: fmtINR(r?.totalCollected) },
        { label: 'Average receipt', value: fmtINR(r?.averageReceiptValue) },
        { label: 'Patients paying', value: (r?.distinctPatients ?? 0).toLocaleString() },
      ]} />
    </>
  );
}

// ─── Patients (sessions + reviews + pitching + renewals) ───
const PATIENT_STATUS_FILTERS = [
  { key: '', label: 'All statuses' },
  { key: 'incoming', label: 'Incoming Appointment' },
  { key: 'followup', label: 'Follow-Up' },
  { key: 'noshow', label: 'No-Show' },
  { key: 'inactive', label: 'Inactive' },
  { key: 'new', label: 'New Patient' },
];

function PatientReport() {
  const dr = useReportRange();
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState(null);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  // This tab used to ignore the global date picker entirely and never send the status/search
  // filters the backend already supported. Both are wired now.
  //
  // Spread the whole range (not just from/to) so excludeHomeopathy reaches the server too — the
  // same "picking out from/to drops it" mistake already fixed for the Staff drill-down elsewhere
  // on this page. Cherry-picking here meant the toggle visibly worked on every other Reports tab
  // except this one.
  const listArgs = useMemo(() => ({
    page, pageSize: 50, sortBy: 'code', sortDesc: true,
    ...dr.range,
    ...(status ? { status } : {}),
    ...(search.trim() ? { search: search.trim() } : {}),
  }), [page, dr.range, status, search]);

  useEffect(() => { setPage(1); }, [status, search, dr.range]);

  const { data, isFetching } = usePatientSessionsQuery(listArgs);
  const { data: eng } = usePatientEngagementQuery(dr.range);
  const [triggerPatients] = useLazyPatientSessionsQuery();

  const rows = useMemo(() => data?.items ?? [], [data]);
  const total = data?.totalCount ?? 0;

  const reviewSeries = useMemo(
    () => (eng?.reviewsByDay ?? []).map((d) => ({ label: dayLabel(d.date), count: d.count })),
    [eng],
  );

  // Export covers the whole tab, and pulls EVERY page of the patient list rather than the pages
  // scrolled into view.
  const getSections = async () => {
    const all = await fetchAllPages(triggerPatients, { ...listArgs, page: undefined, pageSize: undefined });
    return [
      {
        kind: 'kpi',
        title: 'Patients summary',
        items: [
          { label: 'Total patients', value: eng?.totalPatients ?? 0 },
          { label: 'Active (last 15 days)', value: eng?.activePatients ?? 0 },
          { label: 'New in range', value: eng?.newPatientsInRange ?? 0 },
          { label: 'Google reviews in range', value: eng?.reviewsInRange ?? 0 },
          { label: 'Google reviews all time', value: eng?.reviewsAllTime ?? 0 },
          { label: 'Renewal pitches in range', value: eng?.pitchesInRange ?? 0 },
          { label: 'Pitch → renewal rate', value: pct(eng?.pitchConversionRate) },
          { label: 'Patients renewed in range', value: eng?.patientsRenewedInRange ?? 0 },
          { label: 'Renewal revenue in range', value: fmtINR(eng?.renewalRevenueInRange) },
        ],
      },
      {
        title: 'Google reviews collected (by day)',
        columns: [{ key: 'label', label: 'Day' }, { key: 'count', label: 'Reviews', numeric: true }],
        rows: reviewSeries,
      },
      {
        title: 'Google reviews — detail',
        columns: [
          { key: 'code', label: 'Patient ID' }, { key: 'name', label: 'Patient' },
          { key: 'at', label: 'Review date' }, { key: 'by', label: 'Marked by' },
        ],
        rows: (eng?.reviewDetail ?? []).map((r) => ({
          code: r.patientCode, name: r.patientName, at: fmtDate(r.at), by: r.byName || '—',
        })),
      },
      {
        title: 'Renewal pitches by plan',
        columns: [{ key: 'label', label: 'Plan' }, { key: 'count', label: 'Pitches', numeric: true }],
        rows: eng?.pitchesByPlan ?? [],
      },
      {
        title: 'Renewal pitches by doctor',
        columns: [{ key: 'label', label: 'Doctor' }, { key: 'count', label: 'Pitches', numeric: true }],
        rows: eng?.pitchesByDoctor ?? [],
      },
      {
        title: 'Renewal pitches — detail',
        columns: [
          { key: 'code', label: 'Patient ID' }, { key: 'name', label: 'Patient' },
          { key: 'plan', label: 'Plan pitched' }, { key: 'at', label: 'Pitched on' }, { key: 'by', label: 'Pitched by' },
        ],
        rows: (eng?.pitchDetail ?? []).map((r) => ({
          code: r.patientCode, name: r.patientName, plan: r.detail || '—', at: fmtDate(r.at), by: r.byName || '—',
        })),
      },
      {
        title: 'Patient sessions & dues',
        columns: [
          { key: 'code', label: 'Patient ID' }, { key: 'name', label: 'Patient' },
          { key: 'status', label: 'Status' },
          { key: 'totalSessions', label: 'Total sessions', numeric: true },
          { key: 'completed', label: 'Completed', numeric: true },
          { key: 'remaining', label: 'Remaining', numeric: true },
          { key: 'last', label: 'Last session' },
          { key: 'review', label: 'Google review' },
          { key: 'pitched', label: 'Renewal pitched' },
          { key: 'renewals', label: 'Renewals', numeric: true },
          { key: 'billed', label: 'Total billed', numeric: true, money: true },
          { key: 'paid', label: 'Total paid', numeric: true, money: true },
          { key: 'due', label: 'Outstanding', numeric: true, money: true },
        ],
        rows: all.map((r) => ({
          code: r.patientCode, name: r.patientName, status: r.sessionStatus,
          totalSessions: r.totalSessions, completed: r.completedSessions, remaining: r.remainingSessions,
          last: fmtDate(r.lastSessionAt),
          review: r.googleReviewDone ? (r.googleReviewDoneAt ? fmtDate(r.googleReviewDoneAt) : 'Yes (undated)') : 'No',
          pitched: r.renewalPitched && r.renewalPitched !== 'NotYet'
            ? `${r.renewalPitched}${r.renewalPitchedByName ? ` — ${r.renewalPitchedByName}` : ''}` : '—',
          renewals: r.renewalCount,
          billed: r.totalBilled, paid: r.totalPaid, due: r.outstanding,
        })),
      },
    ];
  };

  return (
    <>
      <ReportToolbar dr={dr}>
        <input className="form-control" style={{ width: 200 }} placeholder="Search name / ID / phone"
          value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="form-control" style={{ width: 190 }} value={status} onChange={(e) => setStatus(e.target.value)}>
          {PATIENT_STATUS_FILTERS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <ExportButtons filename="patients-report" title="Patients Report"
          subtitle={rangeLabel(dr.range.from, dr.range.to)}
          orientation="landscape"
          getSections={getSections} />
      </ReportToolbar>

      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <Kpi label="Total patients" value={(eng?.totalPatients ?? 0).toLocaleString()} accent="var(--blue-700)" />
        <Kpi label="Active (15 days)" value={(eng?.activePatients ?? 0).toLocaleString()} />
        <Kpi label="New in range" value={(eng?.newPatientsInRange ?? 0).toLocaleString()} accent="var(--blue-500)" />
        <Kpi label="Google reviews in range" value={(eng?.reviewsInRange ?? 0).toLocaleString()} accent="var(--green-700)" />
        <Kpi label="Renewal pitches in range" value={(eng?.pitchesInRange ?? 0).toLocaleString()} accent="var(--teal-600)" />
        <Kpi label="Pitch → renewal" value={pct(eng?.pitchConversionRate)} accent="var(--teal-600)" />
        <Kpi label="Patients renewed" value={(eng?.patientsRenewedInRange ?? 0).toLocaleString()} />
        <Kpi label="Renewal revenue" value={fmtINR(eng?.renewalRevenueInRange)} accent="var(--green-700)" />
      </div>

      {/* ── Google reviews ── */}
      <div className="g-2" style={{ marginBottom: 18 }}>
        <div className="card">
          <div className="card-header">
            <h3>Google reviews collected</h3>
            <span className="td-muted" style={{ fontSize: '0.78rem', marginLeft: 'auto' }}>
              {(eng?.reviewsAllTime ?? 0).toLocaleString()} all time
              {(eng?.reviewsUndated ?? 0) > 0 && ` · ${eng.reviewsUndated} undated`}
            </span>
          </div>
          <div className="card-body">
            <ChartBox height={280}>
              <LineChart data={reviewSeries} margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="label" fontSize={11} />
                <YAxis fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="count" name="Reviews" stroke="var(--green-600)" strokeWidth={2} dot={false} />
              </LineChart>
            </ChartBox>
            {(eng?.reviewsUndated ?? 0) > 0 && (
              <div className="td-muted" style={{ fontSize: '0.76rem', marginTop: 6 }}>
                {eng.reviewsUndated} review{eng.reviewsUndated !== 1 ? 's were' : ' was'} recorded before review dates
                were tracked, so they have no date and cannot appear on this chart.
              </div>
            )}
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3>Renewal pitches by plan</h3></div>
          <div className="card-body">
            <ChartBox height={280}>
              <BarChart data={eng?.pitchesByPlan ?? []} margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="label" fontSize={11} />
                <YAxis fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" name="Pitches" fill="var(--teal-600)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartBox>
          </div>
        </div>
      </div>

      <div className="g-2" style={{ marginBottom: 18 }}>
        <div className="card">
          <div className="card-header"><h3>Google reviews — detail</h3></div>
          <div className="tbl-wrap" style={{ border: 'none', maxHeight: 300, overflowY: 'auto' }}>
            <table>
              <thead><tr><th>Patient ID</th><th>Patient</th><th>Review date</th><th>Marked by</th></tr></thead>
              <tbody>
                {(eng?.reviewDetail ?? []).map((r) => (
                  <tr key={`${r.patientId}-${r.at}`}>
                    <td className="td-mono">{r.patientCode}</td>
                    <td className="td-bold">{r.patientName}</td>
                    <td className="td-muted">{fmtDate(r.at)}</td>
                    <td className="td-muted">{r.byName || '—'}</td>
                  </tr>
                ))}
                {(eng?.reviewDetail ?? []).length === 0 && (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>No reviews recorded in range.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3>Renewal pitches — detail</h3></div>
          <div className="tbl-wrap" style={{ border: 'none', maxHeight: 300, overflowY: 'auto' }}>
            <table>
              <thead><tr><th>Patient ID</th><th>Patient</th><th>Plan</th><th>Pitched on</th><th>By</th></tr></thead>
              <tbody>
                {(eng?.pitchDetail ?? []).map((r, i) => (
                  <tr key={`${r.patientId}-${i}`}>
                    <td className="td-mono">{r.patientCode}</td>
                    <td className="td-bold">{r.patientName}</td>
                    <td>{r.detail || '—'}</td>
                    <td className="td-muted">{fmtDate(r.at)}</td>
                    <td className="td-muted">{r.byName ? doctorDisplayName(r.byName) : '—'}</td>
                  </tr>
                ))}
                {(eng?.pitchDetail ?? []).length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>No pitches recorded in range.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Patient list ── */}
      <div className="card">
        <div className="card-header">
          <h3>Patient sessions &amp; dues</h3>
          <span className="td-muted" style={{ fontSize: '0.78rem', marginLeft: 'auto' }}>
            {rows.length.toLocaleString()} of {total.toLocaleString()} shown
          </span>
        </div>
        <div className="tbl-wrap" style={{ border: 'none' }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                <th>Patient ID</th><th>Patient Name</th><th>Status</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th style={{ textAlign: 'right' }}>Done</th>
                <th style={{ textAlign: 'right' }}>Left</th>
                <th>Last Session</th>
                <th>Review</th><th>Pitched</th>
                <th style={{ textAlign: 'right' }}>Renewals</th>
                <th style={{ textAlign: 'right' }}>Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <PatientReportRow key={r.patientId} row={r}
                  expanded={expandedId === r.patientId}
                  onToggle={() => setExpandedId((cur) => (cur === r.patientId ? null : r.patientId))} />
              ))}
              {isFetching && rows.length === 0 && (
                <tr><td colSpan={12} style={{ textAlign: 'center', padding: 24 }}>
                  <div className="skeleton" style={{ height: 18, maxWidth: 260, margin: '0 auto' }} />
                </td></tr>
              )}
              {!isFetching && rows.length === 0 && (
                <tr><td colSpan={12} style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>No patients match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {total > rows.length && (
          <div className="toolbar" style={{ justifyContent: 'center', padding: 10 }}>
            <button className="btn btn-outline btn-sm" disabled={isFetching} onClick={() => setPage((p) => p + 1)}>
              {isFetching ? 'Loading…' : `Load more (${rows.length} of ${total})`}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function PatientReportRow({ row, expanded, onToggle }) {
  return (
    <React.Fragment>
      <tr style={{ cursor: 'pointer' }} onClick={onToggle}>
        <td className="td-muted">{expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</td>
        <td className="td-mono">{row.patientCode}</td>
        <td className="td-bold">{row.patientName}</td>
        <td><span className={`badge ${SESSION_STATUS[row.sessionStatus]?.color || 'b-gray'}`}>{row.sessionStatus}</span></td>
        <td style={{ textAlign: 'right' }}>{row.totalSessions}</td>
        <td style={{ textAlign: 'right' }}>{row.completedSessions}</td>
        <td style={{ textAlign: 'right' }}>{row.remainingSessions}</td>
        <td className="td-muted">{fmtDate(row.lastSessionAt)}</td>
        <td>
          {row.googleReviewDone
            ? <span className="badge b-green" title={row.googleReviewDoneAt ? fmtDate(row.googleReviewDoneAt) : 'Recorded before review dates were tracked'}>
                {row.googleReviewDoneAt ? fmtDate(row.googleReviewDoneAt) : 'Yes'}
              </span>
            : <span className="td-muted">—</span>}
        </td>
        <td>
          {row.renewalPitched && row.renewalPitched !== 'NotYet'
            ? <span className="badge b-blue" title={row.renewalPitchedByName || ''}>{row.renewalPitched}</span>
            : <span className="td-muted">—</span>}
        </td>
        <td style={{ textAlign: 'right' }}>{row.renewalCount || 0}</td>
        <td style={{ textAlign: 'right' }} className={row.outstanding > 0 ? 'td-bold' : 'td-muted'}>
          {row.outstanding > 0 ? fmtINR(row.outstanding) : '—'}
        </td>
      </tr>
      {expanded && (
        <tr><td colSpan={12} style={{ background: 'var(--gray-50, #f8fafc)', padding: 0 }}>
          <PatientSessionDrill patientId={row.patientId} />
        </td></tr>
      )}
    </React.Fragment>
  );
}

function PatientSessionDrill({ patientId }) {
  const { data, isFetching } = useListSessionsQuery({ patientId, pageSize: 200, sortBy: 'scheduledAt', sortDesc: true }, { skip: !patientId });
  const sessions = data?.items ?? [];
  if (isFetching && sessions.length === 0) return <div className="skeleton" style={{ height: 80, margin: 12 }} />;
  if (sessions.length === 0) return <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: '0.83rem' }}>No session history.</div>;
  return (
    <div className="tbl-wrap" style={{ border: 'none', padding: '6px 12px 12px' }}>
      <table>
        <thead><tr><th>Session date</th><th>Doctor / staff</th><th>Type</th><th>Status</th><th>Notes</th></tr></thead>
        <tbody>
          {sessions.map((s) => {
            const meta = SESSION_STATUS[s.status] || { label: s.status, color: 'b-gray' };
            return (
              <tr key={s.id}>
                <td className="td-muted">{fmtDate(s.scheduledAt)}</td>
                <td>{s.therapistName ? doctorDisplayName(s.therapistName) : '—'}</td>
                <td>{s.treatmentTypeName || 'Session'}</td>
                <td><span className={`badge ${meta.color}`}>{meta.label}</span></td>
                <td className="td-muted">{s.notes || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Marketing sources (mirror of the backend MarketingSource enum / Billing dialog options).
const SOURCE_OPTIONS = [
  { value: 'Google', label: 'Google' },
  { value: 'Facebook', label: 'Facebook' },
  { value: 'Instagram', label: 'Instagram' },
  { value: 'WalkIn', label: 'Walk-In' },
  { value: 'Reference', label: 'Reference' },
];

// Inline Source editor for a new-patient row. Edits the first invoice's MarketingSource in place
// (reuses the billing PATCH). Disabled when the patient has no first invoice yet (nothing to attribute).
function SourceCell({ row }) {
  const [update, { isLoading }] = useUpdateInvoiceBillingMutation();
  const onChange = async (e) => {
    const marketingSource = e.target.value;
    if (!marketingSource) return;
    try {
      await update({ id: row.firstInvoiceId, marketingSource }).unwrap();
      toast.success('Source updated');
    } catch (err) {
      toast.error(err?.data?.message || 'Could not update source');
    }
  };
  if (!row.firstInvoiceId) {
    return <span className="td-muted" title="No billing yet — add a bill to attribute a source">—</span>;
  }
  return (
    <select className="form-control" style={{ padding: '2px 6px', fontSize: '0.82rem', minWidth: 120 }}
      value={row.source || ''} onChange={onChange} disabled={isLoading}>
      <option value="" disabled>Set source…</option>
      {SOURCE_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
    </select>
  );
}

// ─── New Patient Analysis ───
function NewPatientReport() {
  const dr = useReportRange();
  const { data: r } = useNewPatientAnalysisQuery(dr.range);
  const byDay = (r?.newByDay ?? []).map((d) => ({ label: dayLabel(d.date), count: d.count }));
  const registrants = r?.registrants ?? [];
  const byDoctor = r?.byDoctor ?? [];
  const bySource = r?.bySource ?? [];
  const paymentModes = r?.paymentModes ?? [];

  return (
    <>
      <ReportToolbar dr={dr}>
        {/* Previously exported only the registrants table — 7 KPIs, the acquisition series and
            three breakdown tables all sat on screen and never reached the file. */}
        <ExportButtons filename="new-patients" title="New Patient Analysis"
          subtitle={rangeLabel(dr.range.from, dr.range.to)}
          orientation="landscape"
          getSections={() => [
            {
              kind: 'kpi',
              title: 'New patient summary',
              items: [
                { label: 'New patients', value: r?.totalNewPatients ?? 0 },
                { label: 'First billings', value: r?.firstBillings ?? 0 },
                { label: 'Settled', value: r?.firstBillingsSettled ?? 0 },
                { label: 'Settlement rate', value: pct(r?.firstBillingSettlementRate) },
                { label: 'Total billed', value: fmtINR(r?.totalBilled) },
                { label: 'Total collected', value: fmtINR(r?.totalCollected) },
                { label: 'Outstanding', value: fmtINR(r?.totalOutstanding) },
              ],
            },
            {
              title: 'New patients per day',
              columns: [
                { key: 'label', label: 'Day' },
                { key: 'count', label: 'New patients', numeric: true, avg: true },
              ],
              rows: byDay,
            },
            {
              title: 'Marketing attribution (by source)',
              columns: [
                { key: 'source', label: 'Source' },
                { key: 'count', label: 'Patients', numeric: true },
                // Shares sum to 100 by construction.
                { key: 'share', label: 'Share (%)', numeric: true, total: false },
                { key: 'amount', label: 'First-billing amount', numeric: true, money: true },
              ],
              rows: bySource.map((s) => ({
                source: s.source || 'Unattributed', count: s.count ?? 0,
                share: s.percentage ?? 0, amount: s.amount ?? 0,
              })),
            },
            {
              title: 'Collections by payment method',
              columns: [
                { key: 'method', label: 'Method' },
                { key: 'count', label: 'Payments', numeric: true },
                { key: 'amount', label: 'Amount', numeric: true, money: true },
              ],
              rows: paymentModes.map((p) => ({ method: p.method, count: p.count ?? 0, amount: p.amount ?? 0 })),
            },
            {
              title: 'New patient intake by doctor',
              columns: [
                { key: 'doctor', label: 'Doctor' },
                { key: 'intakes', label: 'New intakes', numeric: true },
                { key: 'converted', label: 'Converted', numeric: true },
                // A ratio: neither a sum nor an unweighted mean is meaningful.
                { key: 'rate', label: 'Conversion rate', numeric: false },
              ],
              rows: byDoctor.map((d) => ({
                doctor: d.doctorName ? doctorDisplayName(d.doctorName) : 'Unassigned',
                intakes: d.newIntakes ?? 0,
                converted: d.converted ?? 0,
                rate: pct(d.conversionRate),
              })),
            },
            {
              title: 'New registrants',
              columns: [
                { key: 'patientCode', label: 'Patient ID' }, { key: 'patientName', label: 'Name' },
                { key: 'registeredAt', label: 'Registered' }, { key: 'source', label: 'Source' },
                { key: 'doctorName', label: 'Doctor' },
                { key: 'billed', label: 'Billed', numeric: true, money: true },
                { key: 'paid', label: 'Collected', numeric: true, money: true },
                { key: 'settled', label: 'First billing settled' },
              ],
              rows: registrants.map((x) => ({
                ...x, registeredAt: fmtDate(x.registeredAt), source: x.source || '—',
                settled: x.firstBillingSettled ? 'Yes' : 'No',
              })),
            },
          ]} />
      </ReportToolbar>

      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <Kpi label="New patients" value={r?.totalNewPatients ?? 0} />
        <Kpi label="First billings" value={r?.firstBillings ?? 0} accent="var(--blue-700)" />
        <Kpi label="Settled" value={r?.firstBillingsSettled ?? 0} accent="var(--green-500)" />
        <Kpi label="Settlement rate" value={pct(r?.firstBillingSettlementRate)} accent="var(--amber-500)" />
      </div>

      {/* First-billing financials for the new-patient cohort. */}
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <Kpi label="Total billed" value={fmtINR(r?.totalBilled)} accent="var(--blue-700)" />
        <Kpi label="Total collected" value={fmtINR(r?.totalCollected)} accent="var(--green-500)" />
        <Kpi label="Outstanding" value={fmtINR(r?.totalOutstanding)} accent="var(--amber-500)" />
      </div>

      <div className="g-2" style={{ marginBottom: 16 }}>
        <div className="card">
          <div className="card-header"><h3>New patient acquisition</h3></div>
          <div className="card-body">
            <ChartBox height={300}>
              <LineChart data={byDay}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" fontSize={11} /><YAxis fontSize={11} allowDecimals={false} />
                <Tooltip /><Line type="monotone" dataKey="count" name="New patients" stroke="var(--blue-600)" strokeWidth={2} />
              </LineChart>
            </ChartBox>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3>Marketing attribution</h3></div>
          <div className="card-body">
            <ChartBox height={300}>
              <PieChart>
                <Pie data={bySource} dataKey="count" nameKey="source" cx="50%" cy="50%" outerRadius={100} label={(e) => `${e.source} ${pct(e.percentage)}`}>
                  {bySource.map((_, i) => <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />)}
                </Pie>
                <Tooltip /><Legend />
              </PieChart>
            </ChartBox>
          </div>
        </div>
      </div>

      {/* Payment-method mix + source revenue: numeric summaries AND charts. */}
      <div className="g-2" style={{ marginBottom: 16 }}>
        <div className="card">
          <div className="card-header"><h3>Collections by payment method</h3></div>
          <div className="card-body">
            <ChartBox height={280}>
              <BarChart data={paymentModes}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="method" fontSize={11} /><YAxis fontSize={11} />
                <Tooltip formatter={(v) => fmtINR(v)} />
                <Bar dataKey="amount" name="Collected" fill="var(--teal-600, #0d9488)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartBox>
            <div className="tbl-wrap" style={{ border: 'none', marginTop: 8 }}>
              <table>
                <thead><tr><th>Method</th><th style={{ textAlign: 'right' }}>Txns</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
                <tbody>
                  {paymentModes.map((m) => (
                    <tr key={m.method}><td className="td-bold">{m.method}</td><td style={{ textAlign: 'right' }}>{m.count}</td><td style={{ textAlign: 'right' }} className="td-bold">{fmtINR(m.amount)}</td></tr>
                  ))}
                  {paymentModes.length === 0 && <tr><td colSpan={3} style={{ textAlign: 'center', padding: 16, color: 'var(--text-muted)' }}>No payments in range.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3>Billing by source</h3></div>
          <div className="tbl-wrap" style={{ border: 'none' }}>
            <table>
              <thead><tr><th>Source</th><th style={{ textAlign: 'right' }}>Patients</th><th style={{ textAlign: 'right' }}>Share</th><th style={{ textAlign: 'right' }}>Billed</th></tr></thead>
              <tbody>
                {bySource.map((s) => (
                  <tr key={s.source}><td className="td-bold">{s.source}</td><td style={{ textAlign: 'right' }}>{s.count}</td><td style={{ textAlign: 'right' }} className="td-muted">{pct(s.percentage)}</td><td style={{ textAlign: 'right' }} className="td-bold">{fmtINR(s.amount)}</td></tr>
                ))}
                {bySource.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', padding: 16, color: 'var(--text-muted)' }}>No source data.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3>Per-doctor new intakes</h3></div>
        <div className="tbl-wrap" style={{ border: 'none' }}>
          <table>
            <thead><tr><th>Doctor</th><th>New intakes</th><th>Converted (billed)</th><th>Conversion</th></tr></thead>
            <tbody>
              {byDoctor.map((d) => (
                <tr key={d.doctorName}><td className="td-bold">{doctorDisplayName(d.doctorName)}</td><td>{d.newIntakes}</td><td>{d.converted}</td><td><span className="badge b-blue">{pct(d.conversionRate)}</span></td></tr>
              ))}
              {byDoctor.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>No data in range.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h3>New registrants</h3>
          <span className="td-muted" style={{ fontSize: '0.78rem' }}>Source is editable inline — it updates the patient's first bill.</span>
        </div>
        <div className="tbl-wrap" style={{ border: 'none' }}>
          <table>
            <thead><tr><th>Patient ID</th><th>Name</th><th>Registered</th><th>Source</th><th>Doctor</th><th style={{ textAlign: 'right' }}>Billed</th><th style={{ textAlign: 'right' }}>Collected</th><th>First billing</th></tr></thead>
            <tbody>
              {registrants.map((x) => (
                <tr key={x.patientId}>
                  <td className="td-mono">{x.patientCode}</td><td className="td-bold">{x.patientName}</td>
                  <td className="td-muted">{fmtDate(x.registeredAt)}</td>
                  <td><SourceCell row={x} /></td>
                  <td>{x.doctorName ? doctorDisplayName(x.doctorName) : '—'}</td>
                  <td style={{ textAlign: 'right' }}>{fmtINR(x.billed)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--green-700)' }}>{fmtINR(x.paid)}</td>
                  <td>{x.firstBillingSettled ? <span className="badge b-green">Settled</span> : <span className="td-muted">—</span>}</td>
                </tr>
              ))}
              {registrants.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>No new patients in range.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ─── Renewal Performance ───
function RenewalReport() {
  const dr = useReportRange();
  const { data: r } = useRenewalAnalysisQuery(dr.range);
  const { data: ledger } = useRenewalLedgerQuery(dr.range);
  const [openPatientId, setOpenPatientId] = useState(null);
  const [openProvider, setOpenProvider] = useState(null); // provider row expanded for drill-down
  const byProvider = r?.byProvider ?? [];
  const chart = byProvider.slice(0, 10).map((p) => ({ name: doctorDisplayName(p.doctorName).replace('Dr. ', ''), revenue: p.revenue, patients: p.patients }));

  return (
    <>
      <ReportToolbar dr={dr}>
        {/* Was exporting only the 3-column provider table; the KPIs, the per-invoice drill-down
            and the entire renewal ledger were on screen but absent from the file. */}
        <ExportButtons filename="renewals" title="Renewal Performance"
          subtitle={rangeLabel(dr.range.from, dr.range.to)}
          orientation="landscape"
          getSections={() => [
            {
              kind: 'kpi',
              title: 'Renewal summary',
              items: [
                { label: 'Returning patients', value: r?.returningPatients ?? 0 },
                { label: 'Renewal revenue', value: fmtINR(r?.grossRenewalRevenue) },
                { label: 'Active packages', value: r?.activeSessionPackages ?? 0 },
                { label: 'Total renewals (ledger)', value: ledger?.totalRenewals ?? 0 },
                { label: 'Renewal value (ledger)', value: fmtINR(ledger?.totalRenewalRevenue) },
                { label: 'Patients with renewals', value: ledger?.patientsWithRenewals ?? 0 },
                { label: 'Average gap between renewals', value: `${Math.round(ledger?.averageGapDays ?? 0)} days` },
              ],
            },
            {
              title: 'Renewal revenue by provider',
              columns: [
                { key: 'doctorName', label: 'Provider' },
                { key: 'patients', label: 'Patients', numeric: true },
                { key: 'revenue', label: 'Revenue', numeric: true, money: true },
              ],
              rows: byProvider.map((p) => ({ doctorName: p.doctorName, patients: p.patients, revenue: p.revenue })),
            },
            {
              title: 'Renewals by provider — per-invoice detail',
              columns: [
                { key: 'provider', label: 'Provider' },
                { key: 'code', label: 'Patient ID' }, { key: 'patient', label: 'Patient' },
                { key: 'invoice', label: 'Invoice No.' }, { key: 'date', label: 'Renewal date' },
                { key: 'billed', label: 'Billed', numeric: true, money: true },
                { key: 'paid', label: 'Paid', numeric: true, money: true },
                { key: 'basis', label: 'Attributed by' },
              ],
              rows: byProvider.flatMap((p) => (p.details ?? []).map((d) => ({
                provider: p.doctorName,
                code: d.patientCode, patient: d.patientName,
                invoice: d.invoiceNumber || '—', date: fmtDate(d.renewalDate),
                billed: d.billedAmount ?? 0, paid: d.paidAmount ?? 0,
                basis: d.attributedByPitch ? 'Renewal pitch' : 'Attending doctor',
              }))),
            },
            {
              title: 'Renewal ledger — per patient',
              columns: [
                { key: 'code', label: 'Patient ID' }, { key: 'patient', label: 'Patient' },
                { key: 'firstInvoice', label: 'First invoice' },
                { key: 'invoices', label: 'Invoices', numeric: true },
                { key: 'renewals', label: 'Renewals', numeric: true },
                { key: 'lastRenewal', label: 'Last renewal' },
                { key: 'value', label: 'Lifetime renewal value', numeric: true, money: true },
                // Gap days are already a per-patient average; the mean across patients is the
                // meaningful figure, the sum is not.
                { key: 'gap', label: 'Avg gap (days)', numeric: true, total: false, avg: true },
              ],
              rows: (ledger?.patients ?? []).map((p) => ({
                code: p.patientCode, patient: p.patientName,
                firstInvoice: fmtDate(p.firstInvoiceAt), invoices: p.totalInvoices,
                renewals: p.renewalCount, lastRenewal: fmtDate(p.lastRenewalAt),
                value: p.lifetimeRenewalValue ?? 0, gap: Math.round(p.averageGapDays ?? 0),
              })),
            },
          ]} />
      </ReportToolbar>

      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <Kpi label="Returning patients" value={r?.returningPatients ?? 0} accent="var(--blue-700)" />
        <Kpi label="Renewal revenue" value={fmtINR(r?.grossRenewalRevenue)} accent="var(--green-500)" />
        <Kpi label="Active packages" value={r?.activeSessionPackages ?? 0} accent="var(--amber-500)" />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3>Renewal revenue by provider</h3></div>
        <div className="card-body">
          <ChartBox height={320}>
            <BarChart data={chart} margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} />
              <Tooltip formatter={(v, n) => (n === 'revenue' ? fmtINR(v) : v)} /><Legend />
              <Bar dataKey="revenue" name="Revenue" fill="var(--green-500)" radius={[4, 4, 0, 0]} />
              <Bar dataKey="patients" name="Patients" fill="var(--blue-500)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartBox>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <h3>By provider</h3>
          <span className="td-muted" style={{ fontSize: '0.78rem' }}>
            Revenue counts toward whoever PITCHED the renewal (recorded on the patient); rows without
            a recorded pitcher fall back to the attending doctor. Click a row for the per-invoice breakdown.
          </span>
        </div>
        <div className="tbl-wrap" style={{ border: 'none' }}>
          <table>
            <thead><tr><th>Provider</th><th>Returning patients</th><th>Renewal revenue</th></tr></thead>
            <tbody>
              {byProvider.map((p) => (
                <React.Fragment key={p.doctorName}>
                  <tr style={{ cursor: 'pointer' }}
                    onClick={() => setOpenProvider((cur) => (cur === p.doctorName ? null : p.doctorName))}
                    title="Click to see the per-patient / per-invoice breakdown">
                    <td className="td-bold">
                      {openProvider === p.doctorName ? '▾ ' : '▸ '}{doctorDisplayName(p.doctorName)}
                    </td>
                    <td>{p.patients}</td>
                    <td className="td-bold" style={{ color: 'var(--green-700)' }}>{fmtINR(p.revenue)}</td>
                  </tr>
                  {openProvider === p.doctorName && (
                    <tr>
                      <td colSpan={3} style={{ background: 'var(--surface-alt, #fafafa)', padding: '8px 18px' }}>
                        <table style={{ width: '100%' }}>
                          <thead>
                            <tr>
                              <th>Patient</th><th>Invoice</th><th>Date</th>
                              <th style={{ textAlign: 'right' }}>Billed</th><th style={{ textAlign: 'right' }}>Collected</th>
                              <th>Attribution</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(p.details ?? []).map((d) => (
                              <tr key={d.invoiceId}>
                                <td className="td-bold">{d.patientName}<span className="td-muted td-mono" style={{ marginLeft: 6, fontSize: '0.74rem' }}>{d.patientCode}</span></td>
                                <td className="td-mono">{d.invoiceNumber}</td>
                                <td className="td-muted">{fmtDate(d.renewalDate)}</td>
                                <td style={{ textAlign: 'right' }}>{fmtINR(d.billedAmount)}</td>
                                <td style={{ textAlign: 'right', color: 'var(--green-700)' }}>{fmtINR(d.paidAmount)}</td>
                                <td>{d.attributedByPitch
                                  ? <span className="badge b-green">Pitched by them</span>
                                  : <span className="badge b-gray" title="No pitcher recorded on the patient — attributed to the attending doctor">Attending (no pitch recorded)</span>}
                                </td>
                              </tr>
                            ))}
                            {(p.details ?? []).length === 0 && (
                              <tr><td colSpan={6} className="td-muted" style={{ padding: 10 }}>No invoice rows.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {byProvider.length === 0 && <tr><td colSpan={3} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>No renewals in range.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Renewal ledger: derived from invoices grouped by patient (2nd invoice = renewal #1). ── */}
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <Kpi label="Renewals in period" value={ledger?.totalRenewals ?? 0} accent="var(--blue-700)" />
        <Kpi label="Renewal revenue (billed)" value={fmtINR(ledger?.totalRenewalRevenue)} accent="var(--green-500)" />
        <Kpi label="Patients renewed" value={ledger?.patientsWithRenewals ?? 0} accent="var(--amber-500)" />
        <Kpi label="Avg gap between renewals" value={ledger?.averageGapDays != null ? `${ledger.averageGapDays} d` : '—'} accent="var(--blue-500)" />
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Renewal ledger — per patient</h3>
          <span className="td-muted" style={{ fontSize: '0.78rem' }}>
            Derived from invoices grouped by patient: the 1st invoice is the new-patient billing, every one after is a renewal. Click a row for the full renewal list.
          </span>
        </div>
        <div className="tbl-wrap" style={{ border: 'none' }}>
          <table>
            <thead>
              <tr>
                <th>Patient</th><th>First billed</th><th style={{ textAlign: 'right' }}>Invoices</th>
                <th style={{ textAlign: 'right' }}>Renewals</th><th>Last renewal</th>
                <th style={{ textAlign: 'right' }}>Lifetime renewal value</th><th style={{ textAlign: 'right' }}>Avg gap</th>
              </tr>
            </thead>
            <tbody>
              {(ledger?.patients ?? []).map((p) => (
                <React.Fragment key={p.patientId}>
                  <tr style={{ cursor: 'pointer' }} onClick={() => setOpenPatientId((cur) => (cur === p.patientId ? null : p.patientId))}>
                    <td className="td-bold">{p.patientName}<div className="td-muted td-mono" style={{ fontSize: '0.75rem' }}>{p.patientCode}</div></td>
                    <td className="td-muted">{new Date(p.firstInvoiceAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</td>
                    <td style={{ textAlign: 'right' }}>{p.totalInvoices}</td>
                    <td style={{ textAlign: 'right' }} className="td-bold">{p.renewalCount}</td>
                    <td className="td-muted">{p.lastRenewalAt ? new Date(p.lastRenewalAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}</td>
                    <td style={{ textAlign: 'right', color: 'var(--green-700)' }} className="td-bold">{fmtINR(p.lifetimeRenewalValue)}</td>
                    <td style={{ textAlign: 'right' }}>{p.averageGapDays != null ? `${p.averageGapDays} d` : '—'}</td>
                  </tr>
                  {openPatientId === p.patientId && (
                    <tr>
                      <td colSpan={7} style={{ background: 'var(--surface-alt, #fafafa)', padding: '8px 18px' }}>
                        <table style={{ width: 'auto', minWidth: 480 }}>
                          <thead>
                            <tr><th>Renewal #</th><th>Invoice</th><th>Date</th><th style={{ textAlign: 'right' }}>Billed amount</th><th>In period</th></tr>
                          </thead>
                          <tbody>
                            {p.renewals.map((rn) => (
                              <tr key={rn.invoiceId}>
                                <td className="td-bold">#{rn.renewalNumber}</td>
                                <td className="td-mono">{rn.invoiceNumber}</td>
                                <td className="td-muted">{new Date(rn.renewalDate).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</td>
                                <td style={{ textAlign: 'right' }}>{fmtINR(rn.renewalValue)}</td>
                                <td>{rn.inPeriod ? <span className="badge b-green">In period</span> : <span className="badge b-gray">Earlier</span>}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {(ledger?.patients ?? []).length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>No renewal activity in this period.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ─── Staff workload (with single-staff drill-down) ───
function StaffReport() {
  const dr = useReportRange();
  const [staffId, setStaffId] = useState('');
  const [drillId, setDrillId] = useState(null); // doctor whose pro-rata breakdown drawer is open
  const { data: staff = [] } = useStaffPerformanceQuery(dr.range);
  const { data: doctors = [] } = useListDoctorsQuery();
  const chartData = useMemo(
    () => staff.slice(0, 10).map((s) => ({ name: doctorDisplayName(s.fullName).replace('Dr. ', ''), revenue: s.revenueAttributed, sessions: s.sessionsConducted })),
    [staff],
  );

  // When a single staff member is selected, scope the whole view to their individual metrics.
  if (staffId) {
    return (
      <>
        <div className="toolbar" style={{ justifyContent: 'flex-end', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          <select className="form-control" style={{ width: 240 }} value={staffId} onChange={(e) => setStaffId(e.target.value)}>
            <option value="">All staff (overview)</option>
            {doctors.map((d) => <option key={d.id} value={d.id}>{doctorDisplayName(d.fullName)}</option>)}
          </select>
        </div>
        <StaffDetailPanel userId={staffId} range={dr.range} />
      </>
    );
  }

  return (
    <>
      <ReportToolbar dr={dr}>
        <select className="form-control" style={{ width: 220 }} value={staffId} onChange={(e) => setStaffId(e.target.value)}>
          <option value="">All staff (overview)</option>
          {doctors.map((d) => <option key={d.id} value={d.id}>{doctorDisplayName(d.fullName)}</option>)}
        </select>
        <ExportButtons filename="staff-workload" title="Staff Workload"
          subtitle={rangeLabel(dr.range.from, dr.range.to)}
          getSections={() => {
            const sessions = staff.reduce((s, x) => s + (x.sessionsConducted ?? 0), 0);
            const revenue = staff.reduce((s, x) => s + (x.revenueAttributed ?? 0), 0);
            return [
              {
                kind: 'kpi',
                title: 'Staff summary',
                items: [
                  { label: 'Staff members', value: staff.length },
                  { label: 'Sessions conducted', value: sessions },
                  { label: 'Revenue attributed', value: fmtINR(revenue) },
                  // Weighted blended rate — the honest average, not a mean of per-staff rates.
                  { label: 'Average revenue per session', value: fmtINR(sessions ? revenue / sessions : 0) },
                  { label: 'Sessions not linked to a package', value: staff.reduce((s, x) => s + (x.unpricedSessions ?? 0), 0) },
                ],
              },
              {
                title: 'Staff performance',
                columns: [
                  { key: 'name', label: 'Staff' },
                  { key: 'consultations', label: 'Consultations', numeric: true },
                  { key: 'sessions', label: 'Sessions', numeric: true },
                  { key: 'patients', label: 'Patients', numeric: true },
                  { key: 'revenue', label: 'Revenue', numeric: true, money: true },
                  { key: 'unpriced', label: 'Unpriced sessions', numeric: true },
                ],
                rows: staff.map((s) => ({
                  name: doctorDisplayName(s.fullName),
                  consultations: s.consultations, sessions: s.sessionsConducted,
                  patients: s.patientsHandled, revenue: s.revenueAttributed,
                  unpriced: s.unpricedSessions ?? 0,
                })),
              },
            ];
          }} />
      </ReportToolbar>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3>Revenue &amp; sessions by staff</h3></div>
        <div className="card-body">
          <ChartBox height={320}>
            <BarChart data={chartData} margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} />
              <Tooltip formatter={(v, n) => (n === 'revenue' ? fmtINR(v) : v)} /><Legend />
              <Bar dataKey="revenue" name="Revenue" fill="var(--green-500)" radius={[4, 4, 0, 0]} />
              <Bar dataKey="sessions" name="Sessions" fill="var(--blue-500)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartBox>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h3>Staff performance</h3></div>
        <div className="tbl-wrap" style={{ border: 'none' }}>
          <table>
            <thead><tr><th>Staff</th><th title="Completed appointments">Sessions</th><th>Patients</th><th>Revenue</th></tr></thead>
            <tbody>
              {staff.map((s) => (
                <tr
                  key={s.userId}
                  onClick={() => setDrillId(s.userId)}
                  style={{ cursor: 'pointer' }}
                  title="View per-package revenue breakdown"
                >
                  <td className="td-bold">{doctorDisplayName(s.fullName)}<div className="td-muted">{s.roles?.join(' · ')}</div></td>
                  <td>{s.sessionsConducted}</td><td>{s.patientsHandled}</td>
                  <td className="td-bold" style={{ color: 'var(--green-700)' }}>
                    {fmtINR(s.revenueAttributed)}
                    {(s.unpricedSessions ?? 0) > 0 && (
                      <div className="td-muted" style={{ fontSize: '0.7rem', fontWeight: 400 }}>
                        {s.unpricedSessions} session{s.unpricedSessions !== 1 ? 's' : ''} not linked to a package
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {staff.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>No staff data.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <SummaryLine items={[
        { label: 'Staff members', value: staff.length },
        { label: 'Sessions', value: staff.reduce((s, x) => s + (x.sessionsConducted ?? 0), 0).toLocaleString() },
        { label: 'Revenue', value: fmtINR(staff.reduce((s, x) => s + (x.revenueAttributed ?? 0), 0)) },
      ]} />

      {drillId && (
        <DoctorRevenueDrilldown userId={drillId} range={dr.range} onClose={() => setDrillId(null)} />
      )}
    </>
  );
}

// Single-staff drill-down with cross-doctor analytics.
function StaffDetailPanel({ userId, range }) {
  // Spread the whole range so excludeHomeopathy reaches the server too — picking out from/to
  // dropped it, so the drill-down was computed on a different population than the row clicked.
  const { data: d } = useStaffDetailQuery({ userId, ...range });
  if (!d) return <div className="skeleton" style={{ height: 160 }} />;
  return (
    <>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>{doctorDisplayName(d.fullName)}</h2>
        {d.roles?.map((r) => <span key={r} className="badge b-blue">{r}</span>)}
        <span style={{ marginLeft: 'auto' }}>
          {/* Exported only Patient ID + name before; the KPIs, procedure mix, monthly trend and
              recent appointments were all on screen and missing from the file. */}
          <ExportButtons filename={`staff-${(d.fullName || 'doctor').replace(/\s+/g, '-').toLowerCase()}`}
            title={`Staff Report — ${doctorDisplayName(d.fullName)}`}
            subtitle={rangeLabel(range?.from, range?.to)}
            getSections={() => [
              {
                kind: 'kpi',
                title: 'Performance summary',
                items: [
                  { label: 'Unique patients', value: d.uniquePatients ?? 0 },
                  { label: 'New patients acquired', value: d.newPatientsAcquired ?? 0 },
                  { label: 'Renewals under doctor', value: d.renewalsUnderDoctor ?? 0 },
                  { label: 'Return rate', value: pct(d.returnRate) },
                  { label: 'Consultations taken', value: d.consultationsTaken ?? 0 },
                  { label: 'Consultation → treatment rate', value: pct(d.consultationToTreatmentRate) },
                  { label: 'Sessions conducted', value: d.sessionsConducted ?? 0 },
                  { label: 'Revenue attributed', value: fmtINR(d.revenueAttributed) },
                ],
              },
              {
                title: 'Monthly consultations & sessions',
                columns: [
                  { key: 'label', label: 'Month' },
                  { key: 'consultations', label: 'Consultations', numeric: true },
                  { key: 'sessions', label: 'Sessions', numeric: true },
                ],
                rows: (d.monthly ?? []).map((m) => ({
                  label: m.label, consultations: m.consultations ?? 0, sessions: m.sessions ?? 0,
                })),
              },
              {
                title: 'Procedure breakdown',
                columns: [
                  { key: 'name', label: 'Procedure' },
                  { key: 'count', label: 'Sessions', numeric: true },
                ],
                rows: (d.procedureBreakdown ?? []).map((p) => ({ name: p.name, count: p.count })),
              },
              {
                title: 'Patients treated',
                columns: [{ key: 'code', label: 'Patient ID' }, { key: 'name', label: 'Patient' }],
                rows: (d.patientList ?? []).map((p) => ({ code: p.patientCode, name: p.patientName })),
              },
              {
                title: 'Recent appointments',
                columns: [
                  { key: 'at', label: 'Date' }, { key: 'patient', label: 'Patient' },
                  { key: 'procedure', label: 'Procedure' }, { key: 'status', label: 'Status' },
                ],
                rows: (d.recentAppointments ?? []).map((a) => ({
                  at: fmtDate(a.scheduledAt), patient: a.patientName || '—',
                  procedure: procedureLabel(a) || '—', status: a.status || '—',
                })),
              },
            ]} />
        </span>
      </div>
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <Kpi label="Unique patients" value={d.uniquePatients ?? 0} accent="var(--blue-700)" />
        <Kpi label="New patients acquired" value={d.newPatientsAcquired ?? 0} accent="var(--teal-600)" />
        <Kpi label="Renewals under doctor" value={d.renewalsUnderDoctor ?? 0} accent="var(--amber-500)" />
        <Kpi label="Return rate" value={pct(d.returnRate)} />
        <Kpi label="Sessions conducted" value={d.sessionsConducted ?? 0} accent="var(--green-600)" />
        <Kpi label="Revenue (attributed)" value={fmtINR(d.revenueAttributed)} accent="var(--green-700)" />
      </div>
      <div className="g-2">
        <div className="card">
          <div className="card-header"><h3>Patients treated ({d.uniquePatients ?? 0})</h3></div>
          <div className="tbl-wrap" style={{ border: 'none', maxHeight: 360, overflow: 'auto' }}>
            <table>
              <thead><tr><th>Patient ID</th><th>Patient</th></tr></thead>
              <tbody>
                {(d.patientList ?? []).map((p) => <tr key={p.patientId}><td className="td-mono">{p.patientCode}</td><td className="td-bold">{p.patientName}</td></tr>)}
                {(d.patientList ?? []).length === 0 && <tr><td colSpan={2} style={{ textAlign: 'center', padding: 18, color: 'var(--text-muted)' }}>No patients in range.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3>Recent appointments</h3></div>
          <div className="tbl-wrap" style={{ border: 'none', maxHeight: 360, overflow: 'auto' }}>
            <table>
              <thead><tr><th>When</th><th>Patient</th><th>Status</th></tr></thead>
              <tbody>
                {(d.recentAppointments ?? []).map((a) => (
                  <tr key={a.id}><td className="td-muted">{fmtDate(a.scheduledAt)}</td><td>{a.patientName ?? '—'}</td><td><span className="badge b-gray">{a.status}</span></td></tr>
                ))}
                {(d.recentAppointments ?? []).length === 0 && <tr><td colSpan={3} style={{ textAlign: 'center', padding: 18, color: 'var(--text-muted)' }}>No appointments.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Billing ───
function BillingReport() {
  const dr = useReportRange();
  const [excludeHomeo] = useExcludeHomeopathy();
  const { data: s } = useBillingSummaryQuery(dr.range);
  const { data: allProcedures = [] } = useProcedureRevenueQuery(dr.range);
  // Payment modes come from the backend's per-transaction (Payment record) aggregation — the same
  // source the Daily Ops tab uses. This previously fetched up to 1000 invoices and guessed the
  // method from the FIRST entry of a comma-joined string, which both truncated the range and
  // mis-attributed any invoice settled through more than one method.
  const { data: opReport } = useOperationalReportQuery(dr.range);

  // "Collected"/"Outstanding" read from the SAME payment-date-based source as the payment-mode
  // table below, not from BillingSummaryQuery's invoice-issued-date total — otherwise the two
  // could show different figures on the same screen for the same range (a payment settling an
  // older invoice counts in the table but not in an invoice-dated "Collected" KPI). "Total billed"
  // legitimately stays invoice-dated — "how much did we bill this period" is a different, valid
  // question from "how much cash came in" and the two are not expected to match.
  // Global Homeopathy omission — strips homeo procedures so chart, table and totals all tie out.
  const procedures = useMemo(
    () => (excludeHomeo ? allProcedures.filter((p) => !isHomeo(p.name)) : allProcedures),
    [allProcedures, excludeHomeo],
  );
  const topProcedures = useMemo(
    () => [...procedures].sort((a, b) => (b.revenue ?? 0) - (a.revenue ?? 0)).slice(0, 10),
    [procedures],
  );
  // How much was collected via each payment method within the range, per actual payment record.
  const paymentModeBreakdown = useMemo(
    () => (opReport?.billing?.paymentModes ?? [])
      .map((p) => ({ mode: p.method, amount: p.amount ?? 0, count: p.count ?? 0 }))
      .sort((a, b) => b.amount - a.amount),
    [opReport],
  );
  return (
    <>
      <ReportToolbar dr={dr}>
        <ExportButtons filename="billing-report" title="Billing Report"
          subtitle={rangeLabel(dr.range.from, dr.range.to)}
          getSections={() => [
            {
              kind: 'kpi',
              title: 'Billing summary',
              items: [
                { label: 'Total billed', value: fmtINR(s?.totalBilled) },
                { label: 'Collected', value: fmtINR(opReport?.billing?.revenueCollected) },
                { label: 'Outstanding', value: fmtINR(opReport?.billing?.outstandingBalance) },
                { label: 'Overdue', value: fmtINR(s?.overdue) },
                { label: 'Advance balance', value: fmtINR(s?.advanceBalance) },
                { label: 'Invoices', value: s?.invoiceCount ?? 0 },
              ],
            },
            {
              title: 'Monthly billed vs collected',
              columns: [
                { key: 'label', label: 'Month' },
                { key: 'billed', label: 'Billed', numeric: true, money: true },
                { key: 'paid', label: 'Collected', numeric: true, money: true },
              ],
              rows: (s?.byMonth ?? []).map((m) => ({
                label: m.label, billed: m.billed ?? 0, paid: m.paid ?? 0,
              })),
            },
            {
              title: 'Collections by payment method',
              columns: [
                { key: 'mode', label: 'Method' },
                { key: 'count', label: 'Payments', numeric: true },
                { key: 'amount', label: 'Amount', numeric: true, money: true },
              ],
              rows: paymentModeBreakdown,
            },
            {
              title: 'Revenue by procedure',
              columns: [
                { key: 'name', label: 'Procedure' },
                { key: 'quantity', label: 'Sessions billed', numeric: true },
                { key: 'revenue', label: 'Revenue', numeric: true, money: true },
              ],
              rows: procedures.map((p) => ({ name: p.name, quantity: p.quantity, revenue: p.revenue })),
            },
          ]} />
      </ReportToolbar>

      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <Kpi label="Total billed" value={fmtINR(s?.totalBilled)} />
        <Kpi label="Collected" value={fmtINR(opReport?.billing?.revenueCollected)} accent="var(--green-500)" />
        <Kpi label="Outstanding" value={fmtINR(opReport?.billing?.outstandingBalance)} accent="var(--amber-500)" />
        <Kpi label="Invoices" value={s?.invoiceCount ?? 0} />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3>Monthly billed vs collected</h3></div>
        <div className="card-body">
          <ChartBox height={300}>
            <BarChart data={s?.byMonth ?? []}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" fontSize={11} /><YAxis fontSize={11} />
              <Tooltip formatter={(v) => fmtINR(v)} /><Legend />
              <Bar dataKey="billed" name="Billed" fill="var(--blue-500)" radius={[4, 4, 0, 0]} />
              <Bar dataKey="paid" name="Collected" fill="var(--green-500)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartBox>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3>Revenue by procedure / treatment</h3></div>
        <div className="card-body">
          {topProcedures.length === 0 ? (
            <div className="td-muted" style={{ padding: '12px 0' }}>No billed procedures in range.</div>
          ) : (
            <ChartBox height={300}>
              <LineChart data={topProcedures}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" fontSize={11} interval={0} angle={-20} textAnchor="end" height={60} />
                <YAxis fontSize={11} />
                <Tooltip formatter={(v) => fmtINR(v)} />
                <Line type="monotone" dataKey="revenue" name="Revenue" stroke="var(--blue-500)" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ChartBox>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3>Payment mode breakdown</h3></div>
        <div className="card-body">
          {paymentModeBreakdown.length === 0 ? (
            <div className="td-muted" style={{ padding: '12px 0' }}>No payments recorded in range.</div>
          ) : (
            <ChartBox height={260}>
              <BarChart data={paymentModeBreakdown} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" fontSize={11} />
                <YAxis dataKey="mode" type="category" fontSize={11} width={100} />
                <Tooltip formatter={(v) => fmtINR(v)} />
                <Bar dataKey="amount" name="Collected" fill="var(--green-500)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ChartBox>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h3>Procedure-wise revenue</h3></div>
        <div className="tbl-wrap" style={{ border: 'none' }}>
          <table>
            <thead><tr><th>Procedure</th><th>Quantity</th><th>Revenue</th></tr></thead>
            <tbody>
              {procedures.map((p) => (
                <tr key={p.name}><td className="td-bold">{p.name}</td><td>{p.quantity}</td><td className="td-bold">{fmtINR(p.revenue)}</td></tr>
              ))}
              {procedures.length === 0 && <tr><td colSpan={3} style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>No billed procedures in range.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <SummaryLine items={[
        { label: 'Procedures', value: procedures.length },
        { label: 'Total quantity', value: procedures.reduce((x, p) => x + (p.quantity || 0), 0).toLocaleString() },
        { label: 'Total revenue', value: fmtINR(procedures.reduce((x, p) => x + (p.revenue || 0), 0)) },
        { label: 'Collected (range)', value: fmtINR(opReport?.billing?.revenueCollected) },
        { label: 'Outstanding', value: fmtINR(opReport?.billing?.outstandingBalance) },
      ]} />
    </>
  );
}

// ─── Daily operational ───
function DailyOpsReport() {
  const dr = useReportRange();

  // Core operational metrics (appointments by day, by-staff counts, billing KPIs).
  //
  // Every money figure on this tab — the KPI band, the payment-mode table, and the per-day
  // revenue column — now comes from this ONE query. It used to mix two sources: the KPI band
  // read invoice-issued-date totals (BillingSummaryQuery) while the payment-mode table read
  // payment-received-date totals (this same operational report) — so "Payments Received" and
  // "Transactions by payment source" could show entirely different numbers on the same screen
  // for the same range (a payment settling an older invoice counted in the table but not the
  // KPI). Single-sourcing makes that impossible by construction.
  const { data: r } = useOperationalReportQuery(dr.range);

  // Invoice list — drives the Patient Ledger on screen (Payment Mode Breakdown now comes
  // from the backend's per-transaction aggregation instead, see r?.billing?.paymentModes).
  // Capped for render performance only; the export re-fetches every page (see doExport).
  const { data: invoicesData, isFetching: loadingInvoices } = useListInvoicesQuery(
    { from: dr.range.from, to: dr.range.to, pageSize: LEDGER_PAGE_SIZE },
    { skip: !dr.range.from },
  );

  // Exports pull the FULL filtered dataset at click time. These used to be capped pre-fetches
  // (1000/2000 rows) whose truncation silently flowed into both the file AND its totals — a
  // full-financial-year export simply dropped everything past the cap with no warning.
  const [triggerAppointments] = useLazyListAppointmentsQuery();
  const [triggerSessions] = useLazyListSessionsQuery();
  const [triggerCallLogs] = useLazyListCallLogsQuery();
  const [triggerInvoices] = useLazyListInvoicesQuery();

  const { data: callAnalytics } = useCallLogAnalyticsQuery(dr.range);
  const { data: staffData = [] } = useStaffPerformanceQuery(dr.range);
  const { data: procedureRevenueData = [] } = useProcedureRevenueQuery(dr.range);

  const [exportBusy, setExportBusy] = useState(false);

  const daily = (r?.daily ?? []).map((d) => ({ ...d, label: dayLabel(d.date) }));
  const ap = r?.appointments ?? {};
  const pat = r?.patients ?? {};
  const invoices = invoicesData?.items ?? [];

  // Procedures/treatments booked in range — PDF only shows ones actually booked (quantity > 0).
  const bookedProcedures = procedureRevenueData.filter((p) => (p.quantity ?? 0) > 0);

  // ── Payment mode breakdown ───────────────────────────────────────────────
  // Sourced from the backend's per-transaction (Payment record) aggregation, so the count
  // and amount are exact even when an invoice was settled via multiple receipts/methods.
  const paymentModeBreakdown = r?.billing?.paymentModes ?? [];

  // ── Patient ledger ───────────────────────────────────────────────────────
  // One row per invoice: billed → paid → outstanding, as of the date range.
  const patientLedger = useMemo(() =>
    invoices.map((inv) => ({
      patientName: inv.patientName ?? '—',
      totalBilling: inv.totalAmount ?? inv.invoiced ?? inv.totalCost ?? 0,
      amountPaid: inv.amountPaid ?? 0,
      outstanding: Math.max(0, (inv.totalAmount ?? inv.invoiced ?? inv.totalCost ?? 0) - (inv.amountPaid ?? 0)),
      paymentMode: inv.paymentMethods ?? inv.paymentMethod ?? '—',
    }))
  , [invoices]);

  // The on-screen ledger is capped; make that visible rather than letting it look complete.
  const ledgerTotal = invoicesData?.totalCount ?? patientLedger.length;
  const ledgerTruncated = ledgerTotal > patientLedger.length;

  const rangeLabel = [
    dr.range.from ? new Date(dr.range.from).toLocaleDateString('en-IN') : '',
    dr.range.to   ? new Date(dr.range.to).toLocaleDateString('en-IN')   : '',
  ].filter(Boolean).join(' – ');

  // ── Export handlers ──────────────────────────────────────────────────────
  const doExport = async (kind) => {
    if (exportBusy) return;
    setExportBusy(true);
    try {
      const range = { from: dr.range.from, to: dr.range.to };
      const [appointments, sessions, callLogs, exportInvoices] = await Promise.all([
        fetchAllPages(triggerAppointments, range),
        fetchAllPages(triggerSessions, range),
        fetchAllPages(triggerCallLogs, range),
        fetchAllPages(triggerInvoices, range),
      ]);

      const apptSummary = {
        booked:      appointments.length,
        completed:   appointments.filter((a) => ['Completed', 'CheckedOut'].includes(a.status)).length,
        cancelled:   appointments.filter((a) => a.status === 'Cancelled').length,
        noShow:      appointments.filter((a) => a.status === 'NoShow').length,
        rescheduled: appointments.filter((a) => a.status === 'Rescheduled').length,
      };

      const baseFilename = `daily-ops-${dr.range.from?.slice(0, 10) ?? 'report'}`;

      if (kind === 'xlsx') {
        exportToXlsx(baseFilename, [
          // ── Sheet 1: Appointments ──────────────────────────────────────
          {
            name: 'Appointments',
            columns: [
              { key: 'patient', label: 'Patient Name' },
              { key: 'time',    label: 'Appointment Time' },
              { key: 'doctor',  label: 'Assigned Doctor' },
              { key: 'status',  label: 'Status' },
            ],
            rows: appointments.map((a) => ({
              patient: a.patientName ?? '—',
              time:    a.scheduledAt ? new Date(a.scheduledAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—',
              doctor:  a.assignedUserName ? doctorDisplayName(a.assignedUserName) : '—',
              status:  a.status ?? '—',
            })),
            summary: [
              { label: 'Total Booked',    value: apptSummary.booked },
              { label: 'Completed',       value: apptSummary.completed },
              { label: 'Cancelled',       value: apptSummary.cancelled },
              { label: 'No-Show',         value: apptSummary.noShow },
              { label: 'Rescheduled',     value: apptSummary.rescheduled },
            ],
          },
          // ── Sheet 2: Staff Performance ─────────────────────────────────
          // Session-level rows when available; falls back to staff summary rows.
          {
            name: 'Staff Performance',
            columns: sessions.length > 0
              ? [
                  { key: 'therapist',   label: 'Staff / Therapist' },
                  { key: 'patient',     label: 'Patient Name' },
                  { key: 'sessionDate', label: 'Session Date & Time' },
                  { key: 'type',        label: 'Treatment Type' },
                  { key: 'status',      label: 'Status' },
                ]
              : [
                  { key: 'staff',    label: 'Staff Name' },
                  { key: 'sessions', label: 'Sessions' },
                  { key: 'patients', label: 'Patients' },
                  { key: 'revenue',  label: 'Revenue (INR)' },
                ],
            rows: sessions.length > 0
              ? sessions.map((s) => ({
                  therapist:   s.therapistName ?? '—',
                  patient:     s.patientName ?? '—',
                  sessionDate: s.scheduledAt ? new Date(s.scheduledAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—',
                  type:        s.treatmentTypeName ?? 'Session',
                  status:      s.status ?? '—',
                }))
              : staffData.map((s) => ({
                  staff:    doctorDisplayName(s.fullName),
                  sessions: s.sessionsConducted ?? 0,
                  patients: s.patientsHandled ?? 0,
                  revenue:  s.revenueAttributed ?? 0,
                })),
            summary: [
              { label: 'Total Staff',        value: staffData.length },
              { label: 'Total Sessions',     value: staffData.reduce((x, s) => x + (s.sessionsConducted ?? 0), 0) },
              { label: 'Invoiced Amount',    value: fmtINR(r?.billing?.totalBilled) },
            ],
          },
          // ── Sheet 3: Payment Modes ──────────────────────────────────────
          {
            name: 'Payment Modes',
            columns: [
              { key: 'method', label: 'Payment Source' },
              { key: 'count',  label: 'Transactions' },
              { key: 'amount', label: 'Amount' },
            ],
            rows: (r?.billing?.paymentModes ?? []).map((p) => ({
              method: p.method,
              count:  p.count ?? 0,
              amount: p.amount ?? 0,
            })),
            summary: [
              { label: 'Total Transactions', value: (r?.billing?.paymentModes ?? []).reduce((s, p) => s + (p.count ?? 0), 0) },
              { label: 'Total Amount',        value: fmtINR((r?.billing?.paymentModes ?? []).reduce((s, p) => s + (p.amount ?? 0), 0)) },
            ],
          },
          // ── Sheet 4: Call Logs ─────────────────────────────────────────
          {
            name: 'Call Logs',
            columns: [
              { key: 'time',    label: 'Timestamp' },
              { key: 'caller',  label: 'Caller Name / Number' },
              { key: 'attempts',label: 'Call Attempts' },
              { key: 'status',  label: 'Status' },
              { key: 'action',  label: 'Disposition / Action' },
            ],
            rows: callLogs.map((c) => ({
              time:     c.lastCallAt ? new Date(c.lastCallAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—',
              caller:   c.callerName
                          ? `${c.callerName} (${c.phoneNumberRaw ?? c.phoneNumber ?? ''})`.trim()
                          : (c.phoneNumberRaw ?? c.phoneNumber ?? '—'),
              attempts: c.totalCalls ?? 1,
              status:   c.isDiscarded ? 'Discarded' : (c.status ?? '—'),
              action:   c.routedTo ?? (c.status === 'Converted' ? 'Converted to Lead/Patient' : '—'),
            })),
            // Mutually exclusive buckets straight from the analytics endpoint — these sum to
            // Total Calls, unlike the old Converted/Existing/New mix which double-counted.
            summary: [
              { label: 'Total Calls',        value: callAnalytics?.totalLogs ?? callLogs.length },
              { label: 'Converted to Lead',  value: callAnalytics?.convertedToLead ?? 0 },
              { label: 'Existing Patient',   value: callAnalytics?.existingPatientOnly ?? 0 },
              { label: 'Existing Lead',      value: callAnalytics?.existingLeadOnly ?? 0 },
              { label: 'Others',             value: callAnalytics?.otherCalls ?? 0 },
            ],
          },
          // ── Sheet 5: Per-day detail ────────────────────────────────────
          // On screen but previously missing from the workbook entirely.
          {
            name: 'Per-day detail',
            columns: [
              { key: 'date',      label: 'Date' },
              { key: 'booked',    label: 'Booked' },
              { key: 'completed', label: 'Completed' },
              { key: 'cancelled', label: 'Cancelled' },
              { key: 'newPts',    label: 'New patients' },
              { key: 'revenue',   label: 'Revenue collected' },
            ],
            rows: (r?.daily ?? []).map((d) => ({
              date:      fmtDate(d.date),
              booked:    d.booked ?? 0,
              completed: d.completed ?? 0,
              cancelled: d.cancelled ?? 0,
              newPts:    d.newPatients ?? 0,
              revenue:   d.revenueCollected ?? 0,
            })),
            summary: [
              { label: 'Days',            value: (r?.daily ?? []).length },
              { label: 'Total booked',    value: (r?.daily ?? []).reduce((s, d) => s + (d.booked ?? 0), 0) },
              { label: 'Total completed', value: (r?.daily ?? []).reduce((s, d) => s + (d.completed ?? 0), 0) },
              { label: 'Total revenue',   value: fmtINR((r?.daily ?? []).reduce((s, d) => s + (d.revenueCollected ?? 0), 0)) },
            ],
          },
          // ── Sheet 6: Patient billing ledger ────────────────────────────
          // Present in the PDF but absent from the workbook until now.
          {
            name: 'Patient billing ledger',
            columns: [
              { key: 'patient',     label: 'Patient Name' },
              { key: 'totalBilling',label: 'Total Billing' },
              { key: 'amountPaid',  label: 'Amount Paid' },
              { key: 'outstanding', label: 'Outstanding' },
              { key: 'paymentMode', label: 'Payment Mode' },
            ],
            rows: exportInvoices.map((inv) => ({
              patient:      inv.patientName ?? '—',
              totalBilling: inv.totalAmount ?? 0,
              amountPaid:   inv.amountPaid ?? 0,
              outstanding:  Math.max(0, (inv.totalAmount ?? 0) - (inv.amountPaid ?? 0)),
              paymentMode:  inv.paymentMethods ?? '—',
            })),
            summary: [
              { label: 'Invoices',    value: exportInvoices.length },
              { label: 'Billed',      value: fmtINR(exportInvoices.reduce((s, i) => s + (i.totalAmount ?? 0), 0)) },
              { label: 'Collected',   value: fmtINR(exportInvoices.reduce((s, i) => s + (i.amountPaid ?? 0), 0)) },
              { label: 'Outstanding', value: fmtINR(exportInvoices.reduce((s, i) => s + Math.max(0, (i.totalAmount ?? 0) - (i.amountPaid ?? 0)), 0)) },
            ],
          },
        ]);
      } else {
        exportDailyOpsPdf(baseFilename, {
          rangeLabel,
          invoices: exportInvoices,
          billingSummary: {
            totalBilled: r?.billing?.totalBilled,
            totalPaid: r?.billing?.revenueCollected,
            outstanding: r?.billing?.outstandingBalance,
            invoiceCount: r?.billing?.billsGenerated,
          },
          staff: staffData,
          callLogs,
          callSummary: callAnalytics,
          paymentModes: r?.billing?.paymentModes ?? [],
          procedures: bookedProcedures,
        });
      }
    } catch (e) {
      toast.error('Could not generate the export. Please try again.');
    } finally {
      setExportBusy(false);
    }
  };

  return (
    <>
      <ReportToolbar>
        <span style={{ display: 'inline-flex', gap: 6 }}>
          <button type="button" className="btn btn-outline btn-sm" disabled={exportBusy} onClick={() => doExport('xlsx')}>
            <FileSpreadsheet size={14} /> Excel (4 sheets)
          </button>
          <button type="button" className="btn btn-outline btn-sm" disabled={exportBusy} onClick={() => doExport('pdf')}>
            <FileText size={14} /> PDF (3 pages)
          </button>
        </span>
      </ReportToolbar>

      {/* ── Financial + appointment KPIs (date-range specific) ── */}
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <Kpi label="Booked" value={ap.booked ?? 0} />
        <Kpi label="Completed" value={ap.completed ?? 0} accent="var(--green-500)" />
        <Kpi label="Cancelled" value={ap.cancelled ?? 0} accent="var(--red-500)" />
        <Kpi label="Rescheduled" value={ap.rescheduled ?? 0} accent="var(--amber-500)" />
        <Kpi label="Invoiced Amount" value={fmtINR(r?.billing?.totalBilled)} accent="var(--blue-700)" />
        <Kpi label="Payments Received" value={fmtINR(r?.billing?.revenueCollected)} accent="var(--green-700)" />
        <Kpi label="Outstanding" value={fmtINR(r?.billing?.outstandingBalance)} accent="var(--amber-500)" />
        <Kpi label="New Registrations" value={pat.newRegistrations ?? 0} accent="var(--blue-500)" />
      </div>

      {/* ── Appointments per day chart ── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3>Appointments per day</h3></div>
        <div className="card-body">
          <ChartBox height={300}>
            <BarChart data={daily}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" fontSize={11} /><YAxis fontSize={11} allowDecimals={false} />
              <Tooltip /><Legend />
              <Bar dataKey="booked" name="Booked" fill="var(--blue-500)" radius={[3, 3, 0, 0]} />
              <Bar dataKey="completed" name="Completed" fill="var(--green-500)" radius={[3, 3, 0, 0]} />
              <Bar dataKey="cancelled" name="Cancelled" fill="var(--red-500)" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ChartBox>
        </div>
      </div>

      {/* ── By staff + Per-day detail ── */}
      <div className="g-2" style={{ marginBottom: 16 }}>
        <div className="card">
          <div className="card-header"><h3>By staff</h3></div>
          <div className="tbl-wrap" style={{ border: 'none' }}>
            <table>
              {/* A session IS a completed appointment, so a separate "Appointments" (booked, any
                  status) column next to "Sessions" (completed-only) read as two disagreeing counts
                  for the same thing. Revenue — via the same pro-rata formula already used on the
                  Staff Performance tab (package cost ÷ sessions purchased × sessions this staff
                  member completed) — is the more useful second metric here. */}
              <thead><tr><th>Staff</th><th>Sessions</th><th>Revenue Generated</th></tr></thead>
              <tbody>
                {staffData.map((s) => (
                  <tr key={s.userId}>
                    <td className="td-bold">{doctorDisplayName(s.fullName)}</td>
                    <td>{s.sessionsConducted ?? 0}</td>
                    <td className="td-bold" style={{ color: 'var(--green-700)' }}>{fmtINR(s.revenueAttributed ?? 0)}</td>
                  </tr>
                ))}
                {staffData.length === 0 && (
                  <tr><td colSpan={3} style={{ textAlign: 'center', padding: 18, color: 'var(--text-muted)' }}>No data.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3>Per-day detail</h3></div>
          <div className="tbl-wrap" style={{ border: 'none' }}>
            <table>
              <thead><tr><th>Date</th><th>Booked</th><th>Done</th><th>Cancel</th><th>New</th><th>Revenue</th></tr></thead>
              <tbody>
                {daily.map((d) => (
                  <tr key={d.date}>
                    <td className="td-muted">{fmtDate(d.date)}</td>
                    <td>{d.booked}</td><td>{d.completed}</td><td>{d.cancelled}</td>
                    <td>{d.newPatients}</td><td>{fmtINR(d.revenueCollected)}</td>
                  </tr>
                ))}
                {daily.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 18, color: 'var(--text-muted)' }}>No data in range.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Transactions by payment source ── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <h3>Transactions by payment source</h3>
          <span className="td-muted" style={{ fontSize: '0.78rem', marginLeft: 'auto' }}>{rangeLabel}</span>
        </div>
        <div className="tbl-wrap" style={{ border: 'none' }}>
          <table>
            <thead>
              <tr>
                <th>Payment Source</th>
                <th style={{ textAlign: 'right' }}>Transactions</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {paymentModeBreakdown.map(({ method, count, amount }) => (
                <tr key={method}>
                  <td className="td-bold">{method}</td>
                  <td style={{ textAlign: 'right' }}>{count}</td>
                  <td className="td-bold" style={{ textAlign: 'right', color: 'var(--green-700)' }}>{fmtINR(amount)}</td>
                </tr>
              ))}
              {paymentModeBreakdown.length === 0 && (
                <tr><td colSpan={3} style={{ textAlign: 'center', padding: 18, color: 'var(--text-muted)' }}>No payment data for selected range.</td></tr>
              )}
            </tbody>
            {paymentModeBreakdown.length > 0 && (
              <tfoot>
                <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                  <td>Total</td>
                  <td style={{ textAlign: 'right' }}>{paymentModeBreakdown.reduce((s, r) => s + (r.count ?? 0), 0)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--green-700)' }}>
                    {fmtINR(paymentModeBreakdown.reduce((s, r) => s + r.amount, 0))}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* ── Patient billing ledger (NEW) ── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <h3>Patient billing ledger</h3>
          <span className="td-muted" style={{ fontSize: '0.78rem', marginLeft: 'auto' }}>
            {ledgerTruncated
              ? `Showing ${patientLedger.length} of ${ledgerTotal.toLocaleString()} invoices — exports include all`
              : `${patientLedger.length} invoice${patientLedger.length !== 1 ? 's' : ''}`}
            {' · '}{rangeLabel}
          </span>
        </div>
        <div className="td-muted" style={{ fontSize: '0.76rem', padding: '0 16px 10px' }}>
          One row per invoice <strong>raised</strong> in this range, with its lifetime amount paid
          and outstanding balance. This is a different measure from the KPI band and payment-mode
          table above (which count cash actually <strong>received</strong> in this range) — a bill
          raised today may not be paid today, and money collected today may settle an older bill,
          so the two sections are not expected to add up to the same totals.
        </div>
        <div className="tbl-wrap" style={{ border: 'none' }}>
          <table>
            <thead>
              <tr>
                <th>Patient Name</th>
                <th style={{ textAlign: 'right' }}>Total Billing</th>
                <th style={{ textAlign: 'right' }}>Amount Paid</th>
                <th style={{ textAlign: 'right' }}>Outstanding</th>
                <th>Payment Mode</th>
              </tr>
            </thead>
            <tbody>
              {patientLedger.map((row, idx) => (
                <tr key={idx}>
                  <td className="td-bold">{row.patientName}</td>
                  <td style={{ textAlign: 'right' }}>{fmtINR(row.totalBilling)}</td>
                  <td className="td-bold" style={{ textAlign: 'right', color: 'var(--green-700)' }}>{fmtINR(row.amountPaid)}</td>
                  <td className="td-bold" style={{ textAlign: 'right', color: row.outstanding > 0 ? 'var(--amber-600)' : 'var(--text-muted)' }}>
                    {fmtINR(row.outstanding)}
                  </td>
                  <td className="td-muted">{row.paymentMode}</td>
                </tr>
              ))}
              {!loadingInvoices && patientLedger.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>No billing data for selected range.</td></tr>
              )}
              {loadingInvoices && (
                <tr><td colSpan={5} style={{ padding: 12 }}><div className="skeleton" style={{ height: 48 }} /></td></tr>
              )}
            </tbody>
            {patientLedger.length > 0 && (
              <tfoot>
                <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                  <td>Totals</td>
                  <td style={{ textAlign: 'right' }}>{fmtINR(patientLedger.reduce((s, r) => s + r.totalBilling, 0))}</td>
                  <td style={{ textAlign: 'right', color: 'var(--green-700)' }}>{fmtINR(patientLedger.reduce((s, r) => s + r.amountPaid, 0))}</td>
                  <td style={{ textAlign: 'right', color: 'var(--amber-600)' }}>{fmtINR(patientLedger.reduce((s, r) => s + r.outstanding, 0))}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <SummaryLine items={[
        { label: 'Appointments booked',  value: (ap.booked ?? 0).toLocaleString() },
        { label: 'Sessions attended',    value: (ap.completed ?? 0).toLocaleString() },
        { label: 'Cancellations',        value: (ap.cancelled ?? 0).toLocaleString() },
        { label: 'Rescheduled',          value: (ap.rescheduled ?? 0).toLocaleString() },
        { label: 'New patients',         value: (pat.newRegistrations ?? 0).toLocaleString() },
        { label: 'Invoiced amount',      value: fmtINR(r?.billing?.totalBilled) },
        { label: 'Payments received',    value: fmtINR(r?.billing?.revenueCollected) },
        { label: 'Outstanding',          value: fmtINR(r?.billing?.outstandingBalance) },
      ]} />
    </>
  );
}

// ─── Call Logs performance ───
const CALL_STATUS_COLORS = {
  New: '#3b82f6', Reviewed: '#0d9488', Converted: '#16a34a', Discarded: '#9ca3af', SentToOther: '#f59e0b',
};
const hourLabel = (h) => `${((h + 11) % 12) + 1}${h < 12 ? 'a' : 'p'}`;

function CallLogsReport() {
  const dr = useReportRange();
  const { data: a, isFetching } = useCallLogAnalyticsQuery(dr.range);

  const statusData = useMemo(() => (a?.statusDistribution ?? []).filter((s) => s.count > 0), [a]);
  const hourly = useMemo(() => (a?.hourly ?? []).map((h) => ({ ...h, label: hourLabel(h.hour) })), [a]);
  const daily = useMemo(() => (a?.daily ?? []).map((d) => ({ label: dayLabel(d.date), count: d.count })), [a]);
  const peakHour = useMemo(() => {
    if (!a?.hourly?.length) return null;
    const top = a.hourly.reduce((m, h) => (h.count > m.count ? h : m), { hour: 0, count: 0 });
    return top.count > 0 ? top : null;
  }, [a]);

  if (isFetching && !a) {
    return (
      <>
        <div className="kpi-grid" style={{ marginBottom: 16 }}>
          {Array.from({ length: 5 }).map((_, i) => <div key={i} className="skeleton" style={{ height: 80 }} />)}
        </div>
        <div className="skeleton" style={{ height: 300 }} />
      </>
    );
  }

  return (
    <>
      <ReportToolbar dr={dr}>
        {/* Was exporting the status table alone (with a broken summary that returned an object
            and so never rendered). Now carries the exclusive buckets, hourly, daily and routing. */}
        <ExportButtons filename="call-logs-analytics" title="Call Logs Performance"
          subtitle={rangeLabel(dr.range.from, dr.range.to)}
          getSections={() => [
            {
              kind: 'kpi',
              title: 'Call volume (buckets sum to Total Calls)',
              items: [
                { label: 'Total Calls', value: a?.totalLogs ?? 0 },
                { label: 'Converted to Lead', value: a?.convertedToLead ?? 0 },
                { label: 'Existing Patient', value: a?.existingPatientOnly ?? 0 },
                { label: 'Existing Lead', value: a?.existingLeadOnly ?? 0 },
                { label: 'Others', value: a?.otherCalls ?? 0 },
                { label: 'Call attempts', value: a?.totalCallAttempts ?? 0 },
                { label: 'Conversion rate', value: pct(a?.conversionRate) },
                { label: 'New numbers', value: a?.newNumbers ?? 0 },
              ],
            },
            {
              title: 'Status distribution',
              columns: [{ key: 'label', label: 'Status' }, { key: 'count', label: 'Calls', numeric: true }],
              rows: (a?.statusDistribution ?? []).map((s) => ({ label: s.label, count: s.count })),
            },
            {
              title: 'Calls by hour (IST)',
              columns: [{ key: 'label', label: 'Hour' }, { key: 'count', label: 'Calls', numeric: true }],
              rows: hourly.map((h) => ({ label: h.label, count: h.count })),
            },
            {
              title: 'Daily call volume',
              columns: [
                { key: 'label', label: 'Day' },
                // Calls per day is a real operational average worth quoting.
                { key: 'count', label: 'Calls', numeric: true, avg: true },
              ],
              rows: daily,
            },
            {
              title: 'Sent to Other — routing',
              columns: [{ key: 'label', label: 'Routed to' }, { key: 'count', label: 'Calls', numeric: true }],
              rows: (a?.routedBreakdown ?? []).map((x) => ({ label: x.label, count: x.count })),
            },
          ]} />
      </ReportToolbar>

      <CallBreakdown a={a} />

      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <Kpi label="Call attempts" value={(a?.totalCallAttempts ?? 0).toLocaleString()} />
        <Kpi label="Conversion rate" value={pct(a?.conversionRate)} accent="var(--teal-600)" />
        <Kpi label="New numbers" value={(a?.newNumbers ?? 0).toLocaleString()} accent="var(--blue-500)" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="card">
          <div className="card-header"><h3>Status distribution</h3></div>
          <div className="card-body">
            {statusData.length ? (
              <ChartBox height={260}>
                <PieChart>
                  <Pie data={statusData} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={90} label>
                    {statusData.map((s) => <Cell key={s.label} fill={CALL_STATUS_COLORS[s.label] || '#6366f1'} />)}
                  </Pie>
                  <Tooltip /><Legend />
                </PieChart>
              </ChartBox>
            ) : <div className="td-muted" style={{ padding: 16 }}>No calls in range.</div>}
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3>Calls by hour (IST)</h3></div>
          <div className="card-body">
            <ChartBox height={260}>
              <BarChart data={hourly} margin={{ left: 6 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" fontSize={10} interval={1} />
                <YAxis fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" name="Calls" fill="var(--blue-500)" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ChartBox>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3>Daily call volume</h3></div>
        <div className="card-body">
          {daily.length ? (
            <ChartBox height={240}>
              <LineChart data={daily} margin={{ left: 6 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" fontSize={11} />
                <YAxis fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="count" name="Calls" stroke="var(--teal-600)" strokeWidth={2} dot={false} />
              </LineChart>
            </ChartBox>
          ) : <div className="td-muted" style={{ padding: 16 }}>No calls in range.</div>}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card">
          <div className="card-header"><h3>Outcome breakdown</h3></div>
          <div className="tbl-wrap" style={{ border: 'none' }}>
            <table>
              <thead><tr><th>Status</th><th style={{ textAlign: 'right' }}>Calls</th><th style={{ textAlign: 'right' }}>Share</th></tr></thead>
              <tbody>
                {(a?.statusDistribution ?? []).map((s) => (
                  <tr key={s.label}>
                    <td><span className="badge" style={{ background: CALL_STATUS_COLORS[s.label] || '#6366f1', color: '#fff' }}>{s.label}</span></td>
                    <td style={{ textAlign: 'right' }}>{s.count.toLocaleString()}</td>
                    <td style={{ textAlign: 'right' }}>{a?.totalLogs > 0 ? pct(s.count / a.totalLogs) : '0%'}</td>
                  </tr>
                ))}
                {!a?.statusDistribution?.length && <tr><td colSpan={3} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>No data.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3>&quot;Sent to Other&quot; routing</h3></div>
          <div className="tbl-wrap" style={{ border: 'none' }}>
            <table>
              <thead><tr><th>Routed to</th><th style={{ textAlign: 'right' }}>Calls</th></tr></thead>
              <tbody>
                {(a?.routedBreakdown ?? []).map((r) => (
                  <tr key={r.label}><td>{r.label}</td><td style={{ textAlign: 'right' }}>{r.count.toLocaleString()}</td></tr>
                ))}
                {!a?.routedBreakdown?.length && <tr><td colSpan={2} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>No routed calls.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="td-muted" style={{ fontSize: '0.74rem', marginTop: 10, fontStyle: 'italic' }}>
        Note: call direction (inbound/outbound) and per-call duration are not captured by the current
        call-sync schema, so those dimensions are omitted here rather than estimated.
      </div>

      <SummaryLine items={[
        { label: 'Total callers', value: (a?.totalLogs ?? 0).toLocaleString() },
        { label: 'Call attempts', value: (a?.totalCallAttempts ?? 0).toLocaleString() },
        { label: 'Converted', value: (a?.converted ?? 0).toLocaleString() },
        { label: 'Conversion rate', value: pct(a?.conversionRate) },
      ]} />
    </>
  );
}

// ─── Communication (WhatsApp/SMS/Email send performance) ───
const COMM_CHANNEL_COLOR = { Sms: 'b-blue', WhatsApp: 'b-green', Email: 'b-purple' };
const COMM_STATUS_COLOR = { Pending: 'b-gray', Sent: 'b-blue', Delivered: 'b-teal', Opened: 'b-green', Replied: 'b-green', Failed: 'b-red' };
const COMM_STATUS_FILTERS = [
  { key: '', label: 'All statuses' },
  { key: 'Sent', label: 'Sent' },
  { key: 'Delivered', label: 'Successfully Delivered' },
  { key: 'Failed', label: 'Not Delivered' },
];
const FAILURE_COLORS = ['#ef4444', '#f59e0b', '#d97706', '#7c3aed', '#0d9488', '#6366f1', '#ec4899', '#84cc16'];

// Auto-send subjects are "<templateName>:<id>[:<day>]"; manual sends use a plain human subject.
// Mirrors CommunicationsPage's parseTemplateName — small enough to duplicate rather than export
// a helper across an otherwise unrelated feature boundary.
function commTemplateName(subject) {
  if (!subject) return null;
  const m = subject.match(/^([a-z][a-z0-9_]*):.+/i);
  return m ? m[1] : subject;
}

function CommMessageRow({ m }) {
  const tpl = commTemplateName(m.renderedSubject);
  const name = m.recipientPatientName || m.recipientLeadName;
  return (
    <tr>
      <td>
        {name && <div className="td-bold">{name}</div>}
        <div className="td-mono td-muted" style={{ fontSize: '0.78rem' }}>
          {m.recipientPatientCode ? `${m.recipientPatientCode} · ` : ''}{m.recipient}
        </div>
      </td>
      <td><span className={`badge ${COMM_CHANNEL_COLOR[m.channel] || 'b-gray'}`}>{m.channel}</span></td>
      <td>
        {tpl && <div className="td-bold" style={{ fontSize: '0.85rem' }}>{tpl}</div>}
        <div className="td-muted" style={{ maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
          {m.renderedBody}
        </div>
        {m.status === 'Failed' && m.failureReason && (
          <div style={{ color: m.isPlatformBlocked ? 'var(--amber-700)' : 'var(--red-700)', fontSize: '0.76rem', marginTop: 2 }}>
            {m.failureReason}
          </div>
        )}
      </td>
      <td>
        {m.status === 'Failed' && m.isPlatformBlocked
          ? <span className="badge b-amber" title="Sent by the CRM; WhatsApp declined to deliver it.">Blocked by WhatsApp</span>
          : <span className={`badge ${COMM_STATUS_COLOR[m.status] || 'b-gray'}`}>{m.status}</span>}
      </td>
      <td className="td-muted">{m.sentAt ? new Date(m.sentAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}</td>
    </tr>
  );
}

function CommunicationReport() {
  const dr = useReportRange();
  const [statusFilter, setStatusFilter] = useState('');
  const [templateFilter, setTemplateFilter] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  useEffect(() => { setPage(1); }, [statusFilter, templateFilter, dr.range.from, dr.range.to]);

  // Unfiltered-by-template call, purely to source the dropdown's option list — so picking one
  // template doesn't make every OTHER template disappear from the list of things you can pick.
  // RTK Query dedups this against the query below whenever templateFilter is empty, so there's
  // no extra request in the common case.
  const { data: templateSource } = useGetMessageAnalyticsQuery({ from: dr.range.from, to: dr.range.to });
  const templateOptions = useMemo(
    () => (templateSource?.byTemplate ?? []).filter((t) => t.template !== '(no template)'),
    [templateSource],
  );

  // Template scopes the WHOLE report — KPIs, charts and breakdowns — not just the detail list,
  // so "what did new_lead do in this range" is answerable from the cards alone.
  const { data: a, isFetching: aLoading } = useGetMessageAnalyticsQuery({
    from: dr.range.from, to: dr.range.to, ...(templateFilter ? { template: templateFilter } : {}),
  });
  const listArgs = useMemo(() => ({
    page, pageSize, from: dr.range.from, to: dr.range.to,
    ...(statusFilter ? { statusGroup: statusFilter } : {}),
    ...(templateFilter ? { template: templateFilter } : {}),
  }), [page, dr.range.from, dr.range.to, statusFilter, templateFilter]);
  const { data: messages, isFetching: mLoading } = useListMessagesQuery(listArgs);
  const [triggerMessages] = useLazyListMessagesQuery();

  const dailySeries = useMemo(
    () => (a?.byDay ?? []).map((d) => ({ label: dayLabel(d.date), sent: d.sent, delivered: d.delivered, failed: d.failed })),
    [a],
  );
  const failureReasons = a?.failureReasons ?? [];

  const getSections = async () => {
    const all = await fetchAllPages(triggerMessages, { ...listArgs, page: undefined, pageSize: undefined });
    return [
      {
        kind: 'kpi',
        title: 'Send performance',
        items: [
          { label: 'Total messages', value: a?.totalMessages ?? 0 },
          { label: 'Delivered successfully', value: a?.totalDelivered ?? 0 },
          { label: 'Failed', value: a?.totalFailed ?? 0 },
          { label: 'Awaiting confirmation', value: a?.totalPending ?? 0 },
          { label: 'Delivery rate', value: `${a?.deliveryRate ?? 0}%` },
        ],
      },
      {
        title: 'Failure reasons',
        columns: [
          { key: 'reason', label: 'Reason' },
          { key: 'count', label: 'Messages', numeric: true },
          { key: 'share', label: 'Share of failures (%)', numeric: true, total: false },
        ],
        rows: failureReasons.map((f) => ({ reason: f.reason, count: f.count, share: f.percentage })),
      },
      {
        title: 'Daily volume',
        columns: [
          { key: 'label', label: 'Day' },
          { key: 'sent', label: 'Sent', numeric: true },
          { key: 'delivered', label: 'Delivered', numeric: true },
          { key: 'failed', label: 'Failed', numeric: true },
        ],
        rows: dailySeries,
      },
      {
        title: 'Messages — detail',
        columns: [
          { key: 'recipient', label: 'Patient / Recipient' },
          { key: 'channel', label: 'Channel' },
          { key: 'template', label: 'Template / Body' },
          { key: 'status', label: 'Status' },
          { key: 'sentAt', label: 'Sent' },
        ],
        rows: all.map((m) => ({
          recipient: `${m.recipientPatientName || m.recipientLeadName || ''} ${m.recipientPatientCode ? `(${m.recipientPatientCode})` : ''} ${m.recipient}`.trim(),
          channel: m.channel,
          template: commTemplateName(m.renderedSubject) || m.renderedBody,
          status: m.status,
          sentAt: m.sentAt ? fmtDate(m.sentAt) : '—',
        })),
      },
    ];
  };

  if (aLoading && !a) {
    return (
      <>
        <div className="kpi-grid" style={{ marginBottom: 16 }}>
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton" style={{ height: 80 }} />)}
        </div>
        <div className="skeleton" style={{ height: 300 }} />
      </>
    );
  }

  return (
    <>
      <ReportToolbar dr={dr}>
        <ExportButtons filename={templateFilter ? `communication-report-${templateFilter}` : 'communication-report'}
          title="Communication Report"
          subtitle={templateFilter
            ? `${rangeLabel(dr.range.from, dr.range.to)} · Template: ${templateFilter}`
            : rangeLabel(dr.range.from, dr.range.to)}
          orientation="landscape"
          getSections={getSections} />
      </ReportToolbar>

      <div className="kpi-grid" style={{ marginBottom: 8 }}>
        <Kpi label="Total messages" value={(a?.totalMessages ?? 0).toLocaleString()} accent="var(--blue-700)" />
        <Kpi label="Delivered successfully" value={(a?.totalDelivered ?? 0).toLocaleString()} accent="var(--green-700)" />
        <Kpi label="Failed" value={(a?.totalFailed ?? 0).toLocaleString()} accent="var(--red-600, #dc2626)" />
        <Kpi label="Awaiting confirmation" value={(a?.totalPending ?? 0).toLocaleString()} accent="var(--amber-500)" />
        <Kpi label="Delivery rate" value={`${a?.deliveryRate ?? 0}%`} accent="var(--teal-600)" />
      </div>
      <div className="td-muted" style={{ fontSize: '0.76rem', marginBottom: 16 }}>
        Total messages = Delivered + Failed + Awaiting confirmation
        {' '}({(a?.totalDelivered ?? 0).toLocaleString()} + {(a?.totalFailed ?? 0).toLocaleString()} + {(a?.totalPending ?? 0).toLocaleString()} = {(a?.totalMessages ?? 0).toLocaleString()}).
        "Awaiting confirmation" means the CRM dispatched it but WhatsApp/the provider hasn't yet reported delivery or failure — this catches up automatically as delivery-status webhooks arrive.
      </div>

      <div className="g-2" style={{ marginBottom: 18 }}>
        <div className="card">
          <div className="card-header"><h3>Daily send volume</h3></div>
          <div className="card-body">
            {dailySeries.length ? (
              <ChartBox height={300}>
                <LineChart data={dailySeries} margin={{ left: 6 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" fontSize={11} />
                  <YAxis fontSize={11} allowDecimals={false} />
                  <Tooltip /><Legend />
                  <Line type="monotone" dataKey="sent" name="Sent" stroke="var(--blue-600)" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="delivered" name="Delivered" stroke="var(--green-600)" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="failed" name="Failed" stroke="var(--red-600, #dc2626)" strokeWidth={2} dot={false} />
                </LineChart>
              </ChartBox>
            ) : <div className="td-muted" style={{ padding: 16 }}>No messages in range.</div>}
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3>Failure reasons</h3></div>
          <div className="card-body">
            {failureReasons.length ? (
              <ChartBox height={300}>
                <PieChart>
                  <Pie data={failureReasons} dataKey="count" nameKey="reason" cx="50%" cy="50%" outerRadius={100} label>
                    {failureReasons.map((_, i) => <Cell key={i} fill={FAILURE_COLORS[i % FAILURE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip /><Legend />
                </PieChart>
              </ChartBox>
            ) : <div className="td-muted" style={{ padding: 16 }}>No failures in range.</div>}
          </div>
        </div>
      </div>

      <div className="toolbar">
        <select className="form-control" style={{ width: 220 }} value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}>
          {COMM_STATUS_FILTERS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select className="form-control" style={{ width: 240 }} value={templateFilter}
          onChange={(e) => setTemplateFilter(e.target.value)}>
          <option value="">All templates</option>
          {templateOptions.map((t) => (
            <option key={t.template} value={t.template}>{t.template} ({t.count})</option>
          ))}
        </select>
        {templateFilter && (
          <span className="td-muted" style={{ fontSize: '0.78rem', alignSelf: 'center' }}>
            Showing only <strong>{templateFilter}</strong> for {rangeLabel(dr.range.from, dr.range.to)}
          </span>
        )}
      </div>

      <div className="card">
        <div className="card-header"><h3>Messages</h3>
          <span className="td-muted" style={{ fontSize: '0.78rem', marginLeft: 'auto' }}>
            {(messages?.totalCount ?? 0).toLocaleString()} message{(messages?.totalCount ?? 0) !== 1 ? 's' : ''} · {rangeLabel(dr.range.from, dr.range.to)}
          </span>
        </div>
        <div className="tbl-wrap" style={{ border: 'none' }}>
          <table>
            <thead>
              <tr>
                <th>Patient / Recipient</th>
                <th>Channel</th>
                <th>Template / Body</th>
                <th>Status</th>
                <th>Sent</th>
              </tr>
            </thead>
            <tbody>
              {mLoading && (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24 }}>
                  <div className="skeleton" style={{ height: 20, maxWidth: 280, margin: '0 auto' }} />
                </td></tr>
              )}
              {(messages?.items ?? []).map((m) => <CommMessageRow key={m.id} m={m} />)}
              {!mLoading && messages?.items?.length === 0 && (
                <tr><td colSpan={5}><div className="empty-state"><h3>No messages</h3><p>No messages match this range and filter.</p></div></td></tr>
              )}
            </tbody>
          </table>
        </div>

        {messages?.totalCount > pageSize && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 4px 4px' }}>
            <div className="td-muted" style={{ fontSize: '0.8rem' }}>
              Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, messages.totalCount)} of {messages.totalCount}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-outline btn-sm" disabled={page <= 1}
                onClick={() => setPage(page - 1)}>Prev</button>
              <button className="btn btn-outline btn-sm" disabled={page * pageSize >= (messages?.totalCount ?? 0)}
                onClick={() => setPage(page + 1)}>Next</button>
            </div>
          </div>
        )}
      </div>

      <SummaryLine items={[
        { label: 'Total messages', value: (a?.totalMessages ?? 0).toLocaleString() },
        { label: 'Delivered', value: (a?.totalDelivered ?? 0).toLocaleString() },
        { label: 'Failed', value: (a?.totalFailed ?? 0).toLocaleString() },
        { label: 'Awaiting confirmation', value: (a?.totalPending ?? 0).toLocaleString() },
        { label: 'Delivery rate', value: `${a?.deliveryRate ?? 0}%` },
      ]} />
    </>
  );
}
